import React, { useState, useEffect } from 'react';
import { Category, MenuItem, CreateMenuItemResponse, MenuImportProcessResult } from '../../types';
// Variantes de tamaño para el formulario (no exportado)
type SizeFormItem = {
  name: string;
  priceMode: 'absolute' | 'delta';
  value: string; // usamos string para inputs; convertimos a número al enviar
};

import { adminMenuService } from '../../services/adminMenu';
import { adminRestaurantsService } from '../../services/adminRestaurants';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { useApp } from '../../context/AppContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';

// Componente para tarjeta de categoría
const CategoryCard = ({ 
  category, 
  onEdit, 
  onDelete,
  // onToggleActive,
}: {
  category: Category;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  // onToggleActive: (category: Category) => void;
}) => {
  return (
    <div className="group relative bg-white/80 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-lg border border-[#22B6B2]/10 hover:shadow-xl hover:border-[#22B6B2]/20 transition-all duration-300 transform hover:-translate-y-1">
      {/* Fondo con color de la categoría */}
      <div 
        className="absolute inset-0 rounded-xl sm:rounded-2xl opacity-5 transition-opacity duration-300 group-hover:opacity-10"
        style={{ backgroundColor: category.color }}
      ></div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2 sm:mb-3 lg:mb-4">
          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
            <div 
              className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl lg:text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg"
              style={{ 
                backgroundColor: category.color + '20',
                border: `2px solid ${category.color}40`,
                boxShadow: `0 4px 12px ${category.color}30`
              }}
            >
              <span>{category.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#2C4358] truncate mb-1">{category.name}</h3>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full animate-pulse" style={{ backgroundColor: category.color }}></div>
                <p className="text-xs font-medium" style={{ color: category.color }}>Categoría activa</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Información adicional */}
        <div className="mb-2 sm:mb-3 lg:mb-4 p-2 sm:p-3 bg-[#22B6B2]/5 rounded-lg sm:rounded-xl border border-[#22B6B2]/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-[#85929e] space-y-1 sm:space-y-0">
            <span>Color: <span className="font-medium" style={{ color: category.color }}>{category.color}</span></span>
            <span>Icono: <span className="font-medium">{category.icon}</span></span>
          </div>
        </div>
        
        {/* Botones de acción mejorados */}
        <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <button
            onClick={() => onEdit(category)}
            className="flex-1 py-1.5 sm:py-2 lg:py-2.5 px-2 sm:px-3 bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] rounded-lg sm:rounded-xl transition-all duration-300 font-medium text-xs border border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:scale-105"
          >
            <span className="mr-1">✏️</span>
            <span>Editar</span>
          </button>
          <button
            onClick={() => onDelete(category)}
            className="flex-1 py-1.5 sm:py-2 lg:py-2.5 px-2 sm:px-3 bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded-lg sm:rounded-xl transition-all duration-300 font-medium text-xs border border-red-500/20 hover:border-red-500/30 hover:scale-105"
          >
            <span className="mr-1">🗑️</span>
            <span>Eliminar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente para tarjeta de item del menú
const MenuItemCard = ({ 
  item, 
  onEdit, 
  onDelete,
  onToggleAvailability,
  onManageImages
}: {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  onToggleAvailability: (item: MenuItem) => void;
  onManageImages: (item: MenuItem) => void;
}) => {
  return (
    <div className="group relative bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-[#22B6B2]/10 hover:shadow-xl hover:border-[#22B6B2]/20 transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
      {/* Imagen del platillo */}
      <div className="relative h-36 sm:h-44 bg-gradient-to-br from-[#22B6B2]/10 to-[#2C4358]/10">
        {item.images && item.images.length > 0 ? (
          <img
            src={item.images[0].imageUrl}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-4xl sm:text-5xl text-[#85929e]">🍽️</span>
          </div>
        )}
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
        
        {/* Badge de disponibilidad mejorado */}
        <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10">
          <span className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-bold rounded-full border-2 transition-all duration-300 shadow-lg backdrop-blur-sm ${
            item.available 
              ? 'bg-[#22B6B2]/90 text-white border-[#22B6B2] shadow-[#22B6B2]/30' 
              : 'bg-red-500/90 text-white border-red-500 shadow-red-500/30'
          }`}>
            {item.available ? '✅ Disponible' : '❌ No disponible'}
          </span>
        </div>
        
        {/* Indicador de múltiples imágenes */}
        {item.images && item.images.length > 1 && (
          <div className="absolute bottom-3 left-3 z-10">
            <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg border border-white/20">
              📸 +{item.images.length - 1}
            </div>
          </div>
        )}
        
          {/* Tamaños (variantes) */}
          {/* Nota: la edición de tamaños se gestiona en el formulario del modal, no aquí. */}
         
       </div>
       
       <div className="p-4 sm:p-5">
         {/* Header con nombre y precio */}
         <div className="flex items-start justify-between mb-2">
           <h3 className="text-lg sm:text-xl font-bold text-[#2C4358] truncate flex-1 mr-3">{item.name}</h3>
           <div className="bg-[#22B6B2]/10 text-[#22B6B2] px-3 py-1 rounded-xl border border-[#22B6B2]/20 text-sm font-bold whitespace-nowrap">
             ${item.price}
           </div>
         </div>
         
         {/* Descripción en una línea */}
         <p className="text-[#85929e] text-xs sm:text-sm mb-3 line-clamp-1 leading-relaxed">{item.description}</p>
         
         {/* Información en fila compacta */}
         <div className="flex items-center justify-between mb-3">
           <div className="flex items-center space-x-2">
             {/* Categoría */}
             <div className="inline-flex items-center px-2 py-1 bg-[#22B6B2]/10 text-[#2C4358] rounded-lg text-xs font-bold border border-[#22B6B2]/20">
               <span className="mr-1">🍽️</span>
               <span>{item.category}</span>
             </div>
             
             {/* Tiempo de preparación */}
             <div className="flex items-center space-x-1 text-xs text-[#85929e] bg-[#22B6B2]/5 px-2 py-1 rounded-lg border border-[#22B6B2]/20">
               <span>⏱️</span>
               <span>{item.preparationTime && item.preparationTime > 0 ? item.preparationTime : 20}min</span>
             </div>
           </div>
           
           {/* Botón de eliminar */}
           <button
             onClick={() => onDelete(item)}
             className="w-8 h-8 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/30 transition-all duration-300 hover:scale-110 flex items-center justify-center"
           >
             <span className="text-sm">🗑️</span>
           </button>
         </div>
         
         {/* Alérgenos en fila horizontal */}
         {item.allergens && item.allergens.length > 0 && (
           <div className="flex items-center space-x-1 mb-3">
             <span className="text-xs text-[#85929e] font-medium">Alérgenos:</span>
             <div className="flex items-center space-x-1">
               {item.allergens.slice(0, 2).map((allergen, index) => (
                 <span key={index} className="bg-orange-100 text-orange-600 text-xs px-2 py-px rounded-lg font-medium border border-orange-200">
                   {allergen}
                 </span>
               ))}
               {item.allergens.length > 2 && (
                 <span className="bg-orange-100 text-orange-600 text-xs px-2 py-px rounded-lg font-medium border border-orange-200">
                   +{item.allergens.length - 2}
                 </span>
               )}
             </div>
           </div>
         )}
         
         {/* Botones de acción compactos */}
         <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-all duration-300">
           <button
             onClick={() => onToggleAvailability(item)}
             className={`w-8 h-8 rounded-lg transition-all duration-300 border hover:scale-110 flex items-center justify-center ${
               item.available 
                 ? 'bg-[#f7ebd6] text-[#2C4358] border-[#f7ebd6]/50 hover:bg-[#f0e0c0] hover:border-[#f0e0c0]' 
                 : 'bg-red-500/10 text-red-600 border-red-500/20 hover:bg-red-500/20 hover:border-red-500/30'
             }`}
             title={item.available ? 'Desactivar' : 'Activar'}
           >
             <span className="text-sm">{item.available ? '✅' : '❌'}</span>
           </button>
           <button
             onClick={() => onEdit(item)}
             className="w-8 h-8 bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] rounded-lg transition-all duration-300 border border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:scale-110 flex items-center justify-center"
             title="Editar"
           >
             <span className="text-sm">✏️</span>
           </button>
           <button
             onClick={() => onManageImages(item)}
             className="w-8 h-8 bg-purple-500/10 text-purple-600 hover:bg-purple-500/20 rounded-lg transition-all duration-300 border border-purple-500/20 hover:border-purple-500/30 hover:scale-110 flex items-center justify-center"
             title="Gestionar imágenes"
           >
             <span className="text-sm">🖼️</span>
           </button>
         </div>
       </div>
     </div>
   );
 }; 

// Componente para cada imagen sortable
function SortableImage({ image, index, onDelete, id, name }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group/image cursor-grab"
    >
      <div className="aspect-square rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-purple-500/20 group-hover/image:border-purple-500/40">
        <img
          src={image.imageUrl}
          alt={`${name} - Imagen ${index + 1}`}
          className="object-cover w-full h-full"
        />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(image.imageUrl);
        }}
        className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 shadow-lg transition-all duration-300 transform hover:scale-110 opacity-0 group-hover/image:opacity-100"
        title="Eliminar imagen"
      >
        ✕
      </button>
      <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg">
        #{index + 1}
      </div>
      {/* Indicador de drag */}
      <div className="absolute top-2 right-2 bg-purple-500/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
        ↕️ Arrastrar
      </div>
    </div>
  );
}

// Componente para cada imagen de preview sortable
function SortablePreviewImage({ file, index, previewUrl, onDelete, id, uploadProgress, uploadStatus }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.7 : 1,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'uploading': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'uploading': return '⏳';
      default: return '⏸️';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group/image cursor-grab"
    >
      <div className="aspect-square rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-[#22B6B2]/20 group-hover/image:border-[#22B6B2]/40">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`Preview ${file.name}`}
            className="object-cover w-full h-full"
          />
        ) : (
          <span className="text-[#85929e] text-3xl">📷</span>
        )}
        
        {/* Progress bar overlay */}
        {uploadStatus && uploadStatus !== 'pending' && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl mb-2">{getStatusIcon(uploadStatus)}</div>
              {uploadStatus === 'uploading' && uploadProgress !== undefined && (
                <div className="w-32 bg-white/20 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
              <div className="text-white text-xs font-medium">
                {uploadStatus === 'uploading' && `${uploadProgress}%`}
                {uploadStatus === 'success' && 'Completado'}
                {uploadStatus === 'error' && 'Error'}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute -top-2 -right-2 w-8 h-8 bg-[#22B6B2] text-white rounded-full flex items-center justify-center text-sm hover:bg-[#2C4358] shadow-lg transition-all duration-300 transform hover:scale-110 opacity-0 group-hover/image:opacity-100"
        title="Eliminar imagen"
      >
        ✕
      </button>
      
      <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg">
        #{index + 1}
      </div>
      
      {/* Indicador de drag */}
      <div className="absolute top-2 right-2 bg-[#22B6B2]/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
        ↕️ Arrastrar
      </div>
      
      {/* Status indicator */}
      {uploadStatus && (
        <div className={`absolute top-2 left-2 w-3 h-3 rounded-full ${getStatusColor(uploadStatus)} animate-pulse`}></div>
      )}
    </div>
  );
}

export default function MenuPage() {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const { categories, menuItems, loadCategories, loadMenu, dispatch, invalidateCache, loading: appLoading } = useApp();

  // Configuración de sensors para dnd-kit (debe estar al inicio del componente)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Estados locales
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>('categories');
  const [selectedCategoryFilter] = useState<string>('all');

  // Estados del modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState<'category' | 'item'>('category');
  const [editingItem, setEditingItem] = useState<Category | MenuItem | null>(null);
  
  // Estado para modal de categorías sugeridas
  const [showSuggestedCategoriesModal, setShowSuggestedCategoriesModal] = useState(false);
  
  // Estados del modal de imágenes
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedItemForImages, setSelectedItemForImages] = useState<MenuItem | null>(null);
  const [itemImages, setItemImages] = useState<any[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<any>(null);
  const [newImagesForUpload, setNewImagesForUpload] = useState<File[]>([]);
  const [optimizeNewImagesOnUpload, setOptimizeNewImagesOnUpload] = useState(false);
  const [uploadingNewImages, setUploadingNewImages] = useState(false);

  // Estados del formulario
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    icon: '🍽️',
    color: '#22b6b2',
  });

  const [newMenuItemData, setNewMenuItemData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    available: true,
    allergens: '',
    preparation_time: '',
    ingredients: [] as string[],
    nutritional_info: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0
    },
    images: [] as string[],
    sizes: [] as SizeFormItem[],
  });

  // Estados para manejo de imágenes
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [optimizeImagesOnCreate, setOptimizeImagesOnCreate] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<{[key: string]: number}>({});
  const [imageUploadStatus, setImageUploadStatus] = useState<{[key: string]: 'pending' | 'uploading' | 'success' | 'error'}>({});

  // Estados para importación de menú con IA
  const [importImages, setImportImages] = useState<File[]>([]);
  const [importPreviewUrls, setImportPreviewUrls] = useState<string[]>([]);
  const [importingMenu, setImportingMenu] = useState(false);
  const [importResult, setImportResult] = useState<MenuImportProcessResult | null>(null);
  const [importSession, setImportSession] = useState<any>(null);
  const [loadingImportSession, setLoadingImportSession] = useState(false);
  const [importPreviewImage, setImportPreviewImage] = useState<{ url: string; name?: string } | null>(null);

  // Estados para selector de color en modal de categorías
  const [showCategoryColorPicker, setShowCategoryColorPicker] = useState(false);

  // Categorías sugeridas por defecto
  const suggestedCategories = [
    { name: 'Entradas', icon: '🥗', color: '#22b6b2' },
    { name: 'Platos Principales', icon: '🍽️', color: '#f59e0b' },
    { name: 'Postres', icon: '🍰', color: '#ec4899' },
    { name: 'Bebidas', icon: '🥤', color: '#3b82f6' },
    { name: 'Acompañamientos', icon: '🍟', color: '#10b981' },
    { name: 'Especialidades', icon: '⭐', color: '#8b5cf6' }
  ];

  // Estado para categorías personalizables
  const [customizableCategories, setCustomizableCategories] = useState(suggestedCategories);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Estado para controlar si ya se mostró el modal de primera vez
  const [hasShownFirstTimeModal, setHasShownFirstTimeModal] = useState(false);

  // Estados para el modal de error
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  // Helper para abrir modal usando mensaje del backend (4xx)
  const showApiError = (title: string, fallbackMessage: string, err: unknown) => {
    let messageToShow = fallbackMessage;
    if (err instanceof ApiError && err.message) {
      messageToShow = err.message;
    } else if (typeof err === 'object' && err && 'message' in (err as any) && typeof (err as any).message === 'string') {
      messageToShow = (err as any).message || fallbackMessage;
    }
    const details = typeof err === 'object' && err && 'message' in (err as any) ? (err as any).message as string : undefined;
    setErrorModal({ open: true, title, message: messageToShow, details });
  };

  // Cargar datos cuando cambie el restaurante
  useEffect(() => {
    if (selectedRestaurantId && user?.uid) {
      console.log('🔄 MenuPage - Cambio de restaurante detectado, cargando datos...');
      loadData();
      // Resetear la bandera del modal cuando cambie de restaurante
      setHasShownFirstTimeModal(false);
    }
  }, [selectedRestaurantId, user?.uid]);

  // Mostrar modal de categorías sugeridas cuando no hay categorías (solo la primera vez)
  useEffect(() => {
    console.log('🔍 Debug modal - Estado actual:', {
      categoriesLength: categories.length,
      loading,
      appLoading,
      selectedRestaurantId,
      userUid: user?.uid,
      hasShownFirstTimeModal,
      shouldShow: categories.length === 0 && !loading && !appLoading && selectedRestaurantId && user?.uid && !hasShownFirstTimeModal
    });
    
    // Si hay categorías, no mostrar el modal
    if (categories.length > 0) {
      console.log('📋 Hay categorías existentes - No mostrar modal');
      return;
    }
    
    // Solo mostrar el modal si:
    // 1. No hay categorías
    // 2. No está cargando (ni local ni global)
    // 3. Hay restaurante seleccionado
    // 4. Hay usuario autenticado
    // 5. No se ha mostrado antes
    // 6. Y además, agregar un pequeño delay para asegurar que la carga haya terminado
    if (categories.length === 0 && !loading && !appLoading && selectedRestaurantId && user?.uid && !hasShownFirstTimeModal) {
      console.log('🎯 Condiciones cumplidas - Programando modal de sugerencias');
      
      // Agregar un pequeño delay para asegurar que todo esté listo
      const timer = setTimeout(() => {
        console.log('🎯 Mostrando modal de sugerencias después del delay');
        setShowSuggestedCategoriesModal(true);
        setHasShownFirstTimeModal(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [categories.length, loading, appLoading, selectedRestaurantId, user?.uid, hasShownFirstTimeModal]);

  const loadData = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  console.log('🔄 MenuPage - Iniciando carga de datos');
  try {
    setLoading(true);
    await Promise.all([
      loadCategories(selectedRestaurantId),
      loadMenu(),
      loadImportSession()
    ]);
    console.log('✅ MenuPage - Datos cargados exitosamente');
  } catch (error) {
    console.error('❌ MenuPage - Error cargando datos:', error);
    showApiError('Error al cargar datos', 'No se pudieron cargar los datos del menú', error);
  } finally {
    setLoading(false);
    console.log('🏁 MenuPage - Carga de datos finalizada');
  }
};

const loadImportSession = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  try {
    setLoadingImportSession(true);
    const restaurant: any = await adminRestaurantsService.getRestaurantById(user.uid, selectedRestaurantId);
    const session = restaurant?.menuImportSession || restaurant?.menu_import_session;
    if (!session?.id) {
      setImportSession(null);
      return;
    }
    const sessionDetail = await adminMenuService.getMenuImportSession(
      user.uid,
      selectedRestaurantId,
      session.id
    );
    setImportSession(sessionDetail);
  } catch (error) {
    console.error('❌ Error cargando sesión de importación:', error);
  } finally {
    setLoadingImportSession(false);
  }
};

const handleAddCategory = () => {
  setModalType('category');
  setNewCategoryData({
    name: '',
    icon: '🍽️',
    color: '#22b6b2',
  });
  setEditingItem(null);
  setShowAddModal(true);
};

const handleAddMenuItem = () => {
  setModalType('item');
  setNewMenuItemData({
    name: '',
    description: '',
    price: '',
    category_id: categories.length > 0 ? categories[0].id : '',
    available: true,
    allergens: '',
    preparation_time: '',
    ingredients: [],
    nutritional_info: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0
    },
    images: [],
    sizes: [],
  });
  setSelectedImages([]);
  setImagePreviewUrls([]);
  setOptimizeImagesOnCreate(false);
  setEditingItem(null);
  setShowAddModal(true);
};

const handleSelectImages = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = true;
  input.onchange = (e) => {
    const files = Array.from((e.target as HTMLInputElement).files || []);
    addImagesToPreview(files);
  };
  input.click();
};

const addImagesToPreview = (files: File[]) => {
  setSelectedImages(prev => [...prev, ...files]);
  files.forEach(file => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreviewUrls(prev => [...prev, result]);
    };
    reader.readAsDataURL(file);
  });
};

const handleDragOver = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(true);
};

const handleDragLeave = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(false);
};

const handleDrop = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(false);
  const files = Array.from(e.dataTransfer.files).filter(file => 
    file.type.startsWith('image/')
  );
  if (files.length > 0) {
    addImagesToPreview(files);
  }
};

const optimizeImageFile = async (file: File): Promise<File> => {
  try {
    if (!file.type || !file.type.startsWith('image/')) return file;

    const type = file.type;
    if (type !== 'image/jpeg' && type !== 'image/png' && type !== 'image/webp') return file;

    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' as any });
    } catch {
      bitmap = await createImageBitmap(file);
    }

    const maxDimension = 1280;
    const originalWidth = bitmap.width || 0;
    const originalHeight = bitmap.height || 0;

    if (!originalWidth || !originalHeight) {
      bitmap.close();
      return file;
    }

    const scale = Math.min(1, maxDimension / Math.max(originalWidth, originalHeight));
    const targetWidth = Math.max(1, Math.round(originalWidth * scale));
    const targetHeight = Math.max(1, Math.round(originalHeight * scale));

    // Si no hay que reescalar y el archivo ya es pequeño, no recomprimir.
    if (scale === 1 && file.size < 250_000) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const quality = type === 'image/webp' ? 0.78 : (type === 'image/jpeg' ? 0.8 : undefined);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
    if (!blob) return file;

    // Si no mejora (o empeora), conservar original.
    if (blob.size >= file.size) return file;

    return new File([blob], file.name, { type, lastModified: file.lastModified });
  } catch {
    return file;
  }
};

const addImportImagesToPreview = (files: File[]) => {
  const allowed = files.filter(file => file.type.startsWith('image/'));
  const next = [...importImages, ...allowed].slice(0, 6);
  setImportImages(next);
  allowed.slice(0, 6 - importImages.length).forEach(file => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImportPreviewUrls(prev => [...prev, result]);
    };
    reader.readAsDataURL(file);
  });
};

const handleSelectImportImages = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = true;
  input.onchange = (e) => {
    const files = Array.from((e.target as HTMLInputElement).files || []);
    addImportImagesToPreview(files);
  };
  input.click();
};

const handleRemoveImportImage = (index: number) => {
  setImportImages(prev => prev.filter((_, idx) => idx !== index));
  setImportPreviewUrls(prev => prev.filter((_, idx) => idx !== index));
};

const handleClearImport = () => {
  setImportImages([]);
  setImportPreviewUrls([]);
  setImportResult(null);
};

const handleDeleteImportSessionImage = async (imageId: string) => {
  if (!user?.uid || !selectedRestaurantId || !importSession?.sessionId) return;
  try {
    const updated = await adminMenuService.deleteMenuImportImage(
      user.uid,
      selectedRestaurantId,
      importSession.sessionId,
      imageId
    );
    setImportSession(updated);
  } catch (error) {
    console.error('❌ Error eliminando imagen de importación:', error);
    showApiError('Error eliminando imagen', 'No se pudo eliminar la imagen de importación', error);
  }
};

const handleImportMenuFromImages = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  if (!importImages.length) {
    setErrorModal({ open: true, title: 'Importación de menú', message: 'Selecciona al menos una imagen.' });
    return;
  }

  try {
    setImportingMenu(true);
    setImportResult(null);

    const session = await adminMenuService.createMenuImportSession(
      user.uid,
      selectedRestaurantId,
      importImages.map((file) => ({
        filename: file.name,
        contentType: file.type || 'image/jpeg',
      }))
    );

    const sessionImages = session.images || [];
    for (let i = 0; i < sessionImages.length; i += 1) {
      const imgMeta = sessionImages[i];
      const imgFile = importImages[i];
      if (!imgFile) continue;
      const uploadResponse = await fetch(imgMeta.signedUrl, {
        method: 'PUT',
        body: imgFile,
        headers: {
          'Content-Type': imgFile.type || 'image/jpeg',
        },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Error subiendo imagen ${imgFile.name}: ${uploadResponse.statusText}`);
      }
    }

    const result = await adminMenuService.processMenuImportSession(
      user.uid,
      selectedRestaurantId,
      session.sessionId
    );

    setImportResult(result);
    await loadImportSession();
    await loadData();
  } catch (error) {
    console.error('❌ Error importando menú con IA:', error);
    showApiError('Error al importar menú', 'No se pudo importar el menú desde las imágenes', error);
  } finally {
    setImportingMenu(false);
  }
};

const handleCreateCategory = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  if (!newCategoryData.name) {
    setErrorModal({ open: true, title: 'Error al crear categoría', message: 'Por favor ingresa el nombre de la categoría' });
    return;
  }
  try {
    // Crear la categoría en el backend
    const newCategory = await adminMenuService.createCategory(user.uid, selectedRestaurantId, newCategoryData);
    
    // Agregar la nueva categoría al array local inmediatamente
    const categoryToAdd: Category = {
      id: newCategory.id || `temp-${Date.now()}`,
      name: newCategory.name || newCategoryData.name,
      icon: newCategory.icon || newCategoryData.icon,
      color: newCategory.color || newCategoryData.color
    };
    
    // Actualizar el estado global inmediatamente usando dispatch
    dispatch({ type: 'SET_CATEGORIES', payload: [...categories, categoryToAdd] });
    
    // Invalidar cache para asegurar sincronización
    invalidateCache('categories');
    
    setShowAddModal(false);
    setNewCategoryData({ name: '', icon: '🍽️', color: '#22b6b2' });
    
    console.log('✅ Categoría creada y agregada al array:', categoryToAdd.name);
  } catch (error) {
    console.error('❌ Error al crear la categoría:', error);
    showApiError('Error al crear categoría', 'Error al crear la categoría', error);
  }
};

// Función para crear categorías sugeridas automáticamente
const handleCreateSuggestedCategories = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  
  try {
    console.log('🚀 Creando categorías sugeridas automáticamente...');
    
    // Crear todas las categorías personalizadas en paralelo
    const createPromises = customizableCategories.map(async (suggestedCategory) => {
      const newCategory = await adminMenuService.createCategory(user.uid, selectedRestaurantId, suggestedCategory);
      return {
        id: newCategory.id || `temp-${Date.now()}`,
        name: newCategory.name || suggestedCategory.name,
        icon: newCategory.icon || suggestedCategory.icon,
        color: newCategory.color || suggestedCategory.color
      };
    });
    
    const createdCategories = await Promise.all(createPromises);
    
    // Actualizar el estado global con todas las categorías creadas
    dispatch({ type: 'SET_CATEGORIES', payload: createdCategories });
    
    // Invalidar cache
    invalidateCache('categories');
    
    console.log('✅ Categorías sugeridas creadas exitosamente:', createdCategories.length);
    setShowSuggestedCategoriesModal(false);
    setCustomizableCategories(suggestedCategories); // Resetear a valores originales
  } catch (error) {
    console.error('❌ Error creando categorías sugeridas:', error);
    showApiError('Error al crear categorías sugeridas', 'Error al crear las categorías sugeridas', error);
  }
};

// Función para actualizar el color de una categoría
const handleUpdateCategoryColor = (index: number, newColor: string) => {
  setCustomizableCategories(prev => 
    prev.map((cat, i) => 
      i === index ? { ...cat, color: newColor } : cat
    )
  );
  setShowColorPicker(false);
  setSelectedCategoryIndex(null);
};

// Función para abrir el selector de color
const handleOpenColorPicker = (index: number) => {
  setSelectedCategoryIndex(index);
  setShowColorPicker(true);
};

// Función para cerrar el selector de color
const handleCloseColorPicker = () => {
  setShowColorPicker(false);
  setSelectedCategoryIndex(null);
};


// Función para abrir selector de color en modal de categorías
const handleOpenCategoryColorPicker = () => {
  setShowCategoryColorPicker(true);
};

// Función para cerrar selector de color en modal de categorías
const handleCloseCategoryColorPicker = () => {
  setShowCategoryColorPicker(false);
};

// Función para actualizar color en modal de categorías
const handleUpdateCategoryModalColor = (newColor: string) => {
  setNewCategoryData(prev => ({ ...prev, color: newColor }));
  setShowCategoryColorPicker(false);
};

// Función para subir una imagen individual con progress tracking
const uploadImageWithProgress = async (file: File, signedUrl: string, fileName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Configurar progress tracking
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setImageUploadProgress(prev => ({ ...prev, [fileName]: progress }));
      }
    });
    
    // Configurar eventos de respuesta
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        setImageUploadStatus(prev => ({ ...prev, [fileName]: 'success' }));
        resolve();
      } else {
        setImageUploadStatus(prev => ({ ...prev, [fileName]: 'error' }));
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      setImageUploadStatus(prev => ({ ...prev, [fileName]: 'error' }));
      reject(new Error('Network error'));
    });
    
    // Iniciar upload
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    setImageUploadStatus(prev => ({ ...prev, [fileName]: 'uploading' }));
    setImageUploadProgress(prev => ({ ...prev, [fileName]: 0 }));
    xhr.send(file);
  });
};

const handleCreateMenuItem = async () => {
  if (!user?.uid || !selectedRestaurantId) return;
  if (!newMenuItemData.name || !newMenuItemData.category_id) {
    setErrorModal({ open: true, title: 'Error al crear platillo', message: 'Por favor completa todos los campos requeridos' });
    return;
  }
  try {
    setUploadingImages(true);
    
    // Limpiar estados de upload anteriores
    setImageUploadProgress({});
    setImageUploadStatus({});
    
    // const { ...menuDataWithoutImages } = newMenuItemData;
    const menuItemData: any = {
      ...newMenuItemData,
      price: parseFloat(newMenuItemData.price) || 0,
      preparation_time: parseInt(newMenuItemData.preparation_time) || 15,
      allergens: newMenuItemData.allergens.split(',').map(a => a.trim()).filter(a => a),
      ingredients: Array.isArray(newMenuItemData.ingredients) ? newMenuItemData.ingredients : [],
      nutritional_info: newMenuItemData.nutritional_info || { calories: 0, protein: 0, carbs: 0, fat: 0 },
      images: selectedImages.map(img => img.name),
      sizes: Array.isArray(newMenuItemData.sizes)
        ? newMenuItemData.sizes
            .filter(s => s.name.trim())
            .map(s => ({
              name: s.name.trim(),
              ...(s.priceMode === 'absolute'
                ? { price: parseFloat(s.value) || 0 }
                : { priceDelta: parseFloat(s.value) || 0 })
            }))
        : []
    };
    
    const newItem = await adminMenuService.createMenuItem(user.uid, selectedRestaurantId, menuItemData) as CreateMenuItemResponse;
    
    if (newItem.images && Array.isArray(newItem.images) && newItem.images.length > 0 && selectedImages.length > 0) {
      const filesForUpload: File[] = [];
      if (optimizeImagesOnCreate) {
        for (const f of selectedImages) {
          filesForUpload.push(await optimizeImageFile(f));
        }
      } else {
        filesForUpload.push(...selectedImages);
      }

      // Crear array de promesas para subir en paralelo
      const uploadPromises = newItem.images.map(async (imgMeta) => {
        const imgFile = filesForUpload.find(img => 
          img.name === imgMeta.altText || 
          img.name === imgMeta.id || 
          img.name === imgMeta.fileName || 
          img.name === imgMeta.name || 
          img.name === imgMeta.id + '.' + imgMeta.extension
        );
        
        if (!imgFile) {
          throw new Error(`No se encontró el archivo para ${imgMeta.altText}`);
        }
        
        return uploadImageWithProgress(imgFile, imgMeta.signedUrl, imgFile.name);
      });
      
      // Subir todas las imágenes en paralelo
      await Promise.allSettled(uploadPromises);
      
      // Verificar si hubo errores
      const hasErrors = Object.values(imageUploadStatus).some(status => status === 'error');
      if (hasErrors) {
        setErrorModal({ open: true, title: 'Error al subir imágenes', message: 'Algunas imágenes no se pudieron subir. Revisa los errores e intenta de nuevo.' });
        return;
      }
    }
    
    // Crear el objeto del nuevo platillo para agregarlo inmediatamente al estado
    const selectedCategory = categories.find(cat => cat.id === newMenuItemData.category_id);
    const newMenuItem: MenuItem = {
      id: newItem.id || `temp-${Date.now()}`,
      name: newItem.name || newMenuItemData.name,
      description: newItem.description || newMenuItemData.description,
      price: newItem.price || parseFloat(newMenuItemData.price) || 0,
      category: selectedCategory?.name || 'Sin categoría',
      categoryId: newItem.categoryId || newMenuItemData.category_id,
      categoryName: selectedCategory?.name || 'Sin categoría',
      categoryIcon: selectedCategory?.icon,
      categoryColor: selectedCategory?.color,
      restaurantId: selectedRestaurantId,
      images: newItem.images || [],
      available: newItem.available !== undefined ? newItem.available : newMenuItemData.available,
      allergens: newItem.allergens || newMenuItemData.allergens.split(',').map(a => a.trim()).filter(a => a),
      preparationTime: newItem.preparationTime || parseInt(newMenuItemData.preparation_time) || 15,
      ingredients: newItem.ingredients || newMenuItemData.ingredients,
      nutritionalInfo: newItem.nutritionalInfo || newMenuItemData.nutritional_info,
      status: 'active'
    };
    
    // Agregar el nuevo platillo al estado global inmediatamente
    dispatch({ type: 'SET_MENU_ITEMS', payload: [...menuItems, newMenuItem] });
    
    // Invalidar cache para asegurar sincronización
    invalidateCache('menu');
    
    setShowAddModal(false);
    setNewMenuItemData({
      name: '', description: '', price: '', category_id: '',
      available: true, allergens: '', preparation_time: '', ingredients: [], nutritional_info: { calories: 0, protein: 0, carbs: 0, fat: 0 }, images: [], sizes: []
    });
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setImageUploadProgress({});
    setImageUploadStatus({});
    
    console.log('✅ Platillo creado y agregado al array:', newMenuItem.name);
  } catch (error) {
    console.error('Error al crear el item del menú:', error);
    showApiError('Error al crear item del menú', 'Error al crear el item del menú', error);
  } finally {
    setUploadingImages(false);
  }
};

const handleEditCategory = (category: Category) => {
  setModalType('category');
  setNewCategoryData({
    name: category.name,
    icon: category.icon,
    color: category.color,
  });
  setEditingItem(category);
  setShowAddModal(true);
};

const handleEditMenuItem = (item: MenuItem) => {
  setModalType('item');
  setNewMenuItemData({
    name: item.name,
    description: item.description,
    price: item.price.toString(),
    category_id: item.categoryId || '',
    available: item.available,
    allergens: Array.isArray(item.allergens) ? item.allergens.join(', ') : (item.allergens || ''),
    preparation_time: item.preparationTime ? item.preparationTime.toString() : '15',
    ingredients: item.ingredients || [],
    nutritional_info: item.nutritionalInfo && typeof item.nutritionalInfo === 'object' && 'calories' in item.nutritionalInfo 
      ? item.nutritionalInfo as { calories: number; protein: number; carbs: number; fat: number }
      : { calories: 0, protein: 0, carbs: 0, fat: 0 },
    images: [], // No cargar imágenes en edición, se gestionan en el modal específico
    sizes: Array.isArray((item as any).sizes)
      ? ((item as any).sizes as Array<{ name: string; price?: number; priceDelta?: number }>)
          .map(s => ({ name: s.name, priceMode: typeof s.price === 'number' ? 'absolute' : 'delta', value: (typeof s.price === 'number' ? s.price : (s.priceDelta || 0)).toString() }))
      : [],
  });
  setEditingItem(item);
  setShowAddModal(true);
  
  console.log('🔍 Cargando datos para editar:', {
    name: item.name,
    description: item.description,
    price: item.price,
    categoryId: item.categoryId,
    available: item.available,
    allergens: item.allergens,
    preparationTime: item.preparationTime,
    ingredients: item.ingredients,
    nutritionalInfo: item.nutritionalInfo
  });
  
  console.log('📝 Datos mapeados al estado:', {
    name: item.name,
    description: item.description,
    price: item.price.toString(),
    category_id: item.categoryId || '',
    available: item.available,
    allergens: Array.isArray(item.allergens) ? item.allergens.join(', ') : (item.allergens || ''),
    preparation_time: item.preparationTime ? item.preparationTime.toString() : '15',
    ingredients: item.ingredients || [],
    nutritional_info: item.nutritionalInfo
  });
};

const handleUpdateCategory = async () => {
  if (!user?.uid || !selectedRestaurantId || !editingItem) return;
  if (!newCategoryData.name) {
    setErrorModal({ open: true, title: 'Error al actualizar categoría', message: 'Por favor ingresa el nombre de la categoría' });
    return;
  }
  try {
    await adminMenuService.updateCategory(
      user.uid, 
      selectedRestaurantId, 
      editingItem.id, 
      newCategoryData
    );
    setShowAddModal(false);
    setEditingItem(null);
    setNewCategoryData({ name: '', icon: '🍽️', color: '#22b6b2' });
    loadData();
  } catch (error) {
    showApiError('Error al actualizar categoría', 'Error al actualizar la categoría', error);
  }
};

const handleUpdateMenuItem = async () => {
  if (!user?.uid || !selectedRestaurantId || !editingItem) return;
  if (!newMenuItemData.name || !newMenuItemData.category_id) {
    setErrorModal({ open: true, title: 'Error al actualizar platillo', message: 'Por favor completa todos los campos requeridos' });
    return;
  }
  try {
    // Construir payload sin imágenes para update
    const { images, ...rest } = newMenuItemData as any;
    const menuItemData: any = {
      ...rest,
      price: parseFloat(newMenuItemData.price) || 0,
      preparation_time: parseInt(newMenuItemData.preparation_time) || 15,
      allergens: newMenuItemData.allergens.split(',').map(a => a.trim()).filter(a => a),
      ingredients: Array.isArray(newMenuItemData.ingredients) ? newMenuItemData.ingredients : [],
      nutritional_info: newMenuItemData.nutritional_info || { calories: 0, protein: 0, carbs: 0, fat: 0 },
      sizes: Array.isArray(newMenuItemData.sizes)
        ? newMenuItemData.sizes
            .filter(s => s.name.trim())
            .map(s => ({
              name: s.name.trim(),
              ...(s.priceMode === 'absolute'
                ? { price: parseFloat(s.value) || 0 }
                : { priceDelta: parseFloat(s.value) || 0 })
            }))
        : []
    };
    
    console.log('📤 Enviando datos al backend:', JSON.stringify(menuItemData, null, 2));
    
    const updatedItem = await adminMenuService.updateMenuItem(
      user.uid, 
      selectedRestaurantId, 
      editingItem.id, 
      menuItemData
    );
    
    // Actualizar el platillo en el estado global inmediatamente
    const selectedCategory = categories.find(cat => cat.id === newMenuItemData.category_id);
    const editingMenuItem = editingItem as MenuItem; // Type assertion ya que sabemos que es un MenuItem en este contexto
    const updatedMenuItem: MenuItem = {
      ...editingMenuItem,
      name: updatedItem.name || newMenuItemData.name,
      description: updatedItem.description || newMenuItemData.description,
      price: updatedItem.price || parseFloat(newMenuItemData.price) || 0,
      category: selectedCategory?.name || 'Sin categoría',
      categoryId: updatedItem.categoryId || newMenuItemData.category_id,
      categoryName: selectedCategory?.name || 'Sin categoría',
      categoryIcon: selectedCategory?.icon,
      categoryColor: selectedCategory?.color,
      restaurantId: selectedRestaurantId,
      images: editingMenuItem.images || [],
      available: updatedItem.available !== undefined ? updatedItem.available : newMenuItemData.available,
      allergens: updatedItem.allergens || newMenuItemData.allergens.split(',').map(a => a.trim()).filter(a => a),
      preparationTime: updatedItem.preparationTime || parseInt(newMenuItemData.preparation_time) || 15,
      ingredients: updatedItem.ingredients || newMenuItemData.ingredients,
      nutritionalInfo: updatedItem.nutritionalInfo || newMenuItemData.nutritional_info,
      status: 'active'
    };
    
    // Actualizar el array de platillos
    const updatedMenuItems = menuItems.map(item => 
      item.id === editingItem.id ? updatedMenuItem : item
    );
    dispatch({ type: 'SET_MENU_ITEMS', payload: updatedMenuItems });
    
    // Invalidar cache
    invalidateCache('menu');
    
    setShowAddModal(false);
    setEditingItem(null);
    setNewMenuItemData({
      name: '', description: '', price: '', category_id: '',
      available: true, allergens: '', preparation_time: '', ingredients: [], nutritional_info: { calories: 0, protein: 0, carbs: 0, fat: 0 }, images: [], sizes: []
    });
    setSelectedImages([]);
    setImagePreviewUrls([]);
    
    console.log('✅ Platillo actualizado:', updatedMenuItem.name);
  } catch (error) {
    showApiError('Error al actualizar item del menú', 'Error al actualizar el item del menú', error);
  }
};

const handleDeleteCategory = async (category: Category) => {
  if (!user?.uid || !selectedRestaurantId) return;
  if (!confirm(`¿Estás seguro de que quieres eliminar la categoría "${category.name}"?`)) {
    return;
  }
  try {
    await adminMenuService.deleteCategory(user.uid, selectedRestaurantId, category.id);
    
    // Eliminar la categoría del estado global inmediatamente
    const updatedCategories = categories.filter(cat => cat.id !== category.id);
    dispatch({ type: 'SET_CATEGORIES', payload: updatedCategories });
    
    // También eliminar los platillos de esa categoría
    const updatedMenuItems = menuItems.filter(item => item.categoryId !== category.id);
    dispatch({ type: 'SET_MENU_ITEMS', payload: updatedMenuItems });
    
    // Invalidar cache
    invalidateCache('categories');
    invalidateCache('menu');
    
    console.log('✅ Categoría eliminada:', category.name);
  } catch (error) {
    console.error('❌ Error al eliminar categoría:', error);
    showApiError('Error al eliminar categoría', 'Error al eliminar la categoría', error);
  }
};

const handleDeleteMenuItem = async (item: MenuItem) => {
  if (!user?.uid || !selectedRestaurantId) return;
  if (!confirm(`¿Estás seguro de que quieres eliminar "${item.name}"?`)) {
    return;
  }
  try {
    await adminMenuService.deleteMenuItem(user.uid, selectedRestaurantId, item.id);
    
    // Eliminar el platillo del estado global inmediatamente
    const updatedMenuItems = menuItems.filter(menuItem => menuItem.id !== item.id);
    dispatch({ type: 'SET_MENU_ITEMS', payload: updatedMenuItems });
    
    // Invalidar cache
    invalidateCache('menu');
    
    console.log('✅ Platillo eliminado:', item.name);
  } catch (error) {
    console.error('❌ Error al eliminar platillo:', error);
    showApiError('Error al eliminar platillo', 'Error al eliminar el item del menú', error);
  }
};

// const handleToggleCategoryActive = async (category: Category) => {
//   if (!user?.uid || !selectedRestaurantId) return;
//   try {
//     loadData();
//   } catch (error) {
//     alert('Error al cambiar el estado de la categoría');
//   }
// };

const handleToggleMenuItemAvailability = async (item: MenuItem) => {
  if (!user?.uid || !selectedRestaurantId) return;
  try {
    await adminMenuService.updateMenuItemAvailability(
      user.uid, 
      selectedRestaurantId, 
      item.id, 
      !item.available
    );
    
    // Actualizar la disponibilidad del platillo en el estado global inmediatamente
    const updatedMenuItems = menuItems.map(menuItem => 
      menuItem.id === item.id 
        ? { ...menuItem, available: !item.available }
        : menuItem
    );
    dispatch({ type: 'SET_MENU_ITEMS', payload: updatedMenuItems });
    
    // Invalidar cache
    invalidateCache('menu');
    
    console.log('✅ Disponibilidad actualizada:', item.name, !item.available);
  } catch (error) {
    console.error('❌ Error al cambiar disponibilidad:', error);
    showApiError('Error al cambiar disponibilidad', 'Error al cambiar la disponibilidad', error);
  }
};

const filteredMenuItems = selectedCategoryFilter === 'all' 
  ? (Array.isArray(menuItems) ? menuItems : [])
  : (Array.isArray(menuItems) ? menuItems.filter(item => item.categoryId === selectedCategoryFilter) : []);

const clearImageState = () => {
  setSelectedImages([]);
  setImagePreviewUrls([]);
  setIsDragging(false);
};

const handleCloseModal = () => {
  setShowAddModal(false);
  setEditingItem(null);
  setModalType('category');
  clearImageState();
};

const handleOpenImageModal = async (item: MenuItem) => {
  if (!user?.uid || !selectedRestaurantId) return;
  
  setSelectedItemForImages(item);
  setShowImageModal(true);
  setLoadingImages(true);
  setOptimizeNewImagesOnUpload(false);
  
  try {
    console.log('🔄 Cargando imágenes del platillo:', item.name);
    const images = await adminMenuService.getMenuItemImages(user.uid, selectedRestaurantId, item.id);
    setItemImages(images);
    console.log('✅ Imágenes cargadas:', images.length);
  } catch (error) {
    console.error('❌ Error cargando imágenes:', error);
    showApiError('Error al cargar imágenes', 'Error al cargar las imágenes del platillo', error);
    setItemImages([]);
  } finally {
    setLoadingImages(false);
  }
};

const handleCloseImageModal = () => {
  setShowImageModal(false);
  setSelectedItemForImages(null);
  setItemImages([]);
  setNewImagesForUpload([]);
  setOptimizeNewImagesOnUpload(false);
  setImageToDelete(null);
  setShowDeleteConfirmModal(false);
};

// const handleReorderImages = (fromIndex: number, toIndex: number) => {
//   const newImages = [...itemImages];
//   const draggedImage = newImages[fromIndex];
//   newImages.splice(fromIndex, 1);
//   newImages.splice(toIndex, 0, draggedImage);
//   setItemImages(newImages);
// };

const handleSaveImageOrder = async () => {
  if (!selectedItemForImages || !user?.uid || !selectedRestaurantId) return;
  
  try {
    console.log('🔄 Guardando orden de imágenes...');
    
    // Extraer los IDs de las imágenes en el nuevo orden
    const imageIds = itemImages.map(img => img.id || img.imageUrl);
    
    // Llamar al servicio para reordenar las imágenes
    await adminMenuService.reorderMenuItemImages(
      user.uid, 
      selectedRestaurantId, 
      selectedItemForImages.id, 
      imageIds
    );
    
    console.log('✅ Orden de imágenes guardado');
    
    // Cerrar el modal después de guardar
    handleCloseImageModal();
    
    // Recargar los datos para reflejar los cambios
    loadData();
    
  } catch (error) {
    console.error('❌ Error al guardar el orden de imágenes:', error);
    showApiError('Error al guardar orden de imágenes', 'Error al guardar el orden de las imágenes. Por favor, intenta de nuevo.', error);
  }
};

// Función para confirmar eliminación de imagen
const handleConfirmDeleteImage = async () => {
  if (!selectedItemForImages || !user?.uid || !selectedRestaurantId || !imageToDelete) return;
  
  try {
    console.log('🗑️ Eliminando imagen:', imageToDelete.id);
    
    await adminMenuService.deleteMenuItemImage(
      user.uid,
      selectedRestaurantId,
      selectedItemForImages.id,
      imageToDelete.id
    );
    
    // Remover la imagen del estado local
    setItemImages(prev => prev.filter(img => img.id !== imageToDelete.id));
    
    console.log('✅ Imagen eliminada');
    setShowDeleteConfirmModal(false);
    setImageToDelete(null);
    
  } catch (error) {
    console.error('❌ Error eliminando imagen:', error);
    showApiError('Error al eliminar imagen', 'Error al eliminar la imagen. Por favor, intenta de nuevo.', error);
  }
};

// Función para abrir modal de confirmación de eliminación
const handleDeleteImage = (image: any) => {
  setImageToDelete(image);
  setShowDeleteConfirmModal(true);
};

// Función para subir nuevas imágenes
const handleUploadNewImages = async () => {
  if (!selectedItemForImages || !user?.uid || !selectedRestaurantId || newImagesForUpload.length === 0) return;
  
  try {
    setUploadingNewImages(true);
    console.log('📤 Subiendo nuevas imágenes...');
    
    // Limpiar estados de progreso anteriores
    setImageUploadProgress({});
    setImageUploadStatus({});
    
    const filesForUpload: File[] = [];
    if (optimizeNewImagesOnUpload) {
      for (const f of newImagesForUpload) {
        filesForUpload.push(await optimizeImageFile(f));
      }
    } else {
      filesForUpload.push(...newImagesForUpload);
    }

    // Subir imágenes usando el servicio con progreso
    await adminMenuService.uploadMenuItemImages(
      user.uid,
      selectedRestaurantId,
      selectedItemForImages.id,
      filesForUpload,
      (current: number, total: number) => {
        // Calcular progreso general como porcentaje
        const overallProgress = Math.round((current / total) * 100);
        console.log(`📊 Progreso: ${current}/${total} (${overallProgress}%)`);
        
        // Actualizar progreso individual para cada imagen
        filesForUpload.forEach((file, index) => {
          if (index < current) {
            setImageUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
            setImageUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
          } else if (index === current) {
            setImageUploadProgress(prev => ({ ...prev, [file.name]: 50 }));
            setImageUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
          } else {
            setImageUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
            setImageUploadStatus(prev => ({ ...prev, [file.name]: 'pending' }));
          }
        });
      }
    );
    
    // Recargar las imágenes del platillo
    const updatedImages = await adminMenuService.getMenuItemImages(
      user.uid,
      selectedRestaurantId,
      selectedItemForImages.id
    );
    setItemImages(updatedImages);
    
    // Limpiar las nuevas imágenes
    setNewImagesForUpload([]);
    
    console.log('✅ Nuevas imágenes subidas');
    
  } catch (error) {
    console.error('❌ Error subiendo nuevas imágenes:', error);
    showApiError('Error al subir nuevas imágenes', 'Error al subir las nuevas imágenes. Por favor, intenta de nuevo.', error);
  } finally {
    setUploadingNewImages(false);
    // Limpiar estados de progreso
    setImageUploadProgress({});
    setImageUploadStatus({});
  }
};

if (!selectedRestaurantId) {
  return (
    <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-gray-100 max-w-md w-full">
        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🏪</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Selecciona un restaurante</h2>
        <p className="text-gray-600 mb-8 leading-relaxed">
          Para gestionar el menú, primero necesitas seleccionar un restaurante de tu lista
        </p>
        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
          <p className="text-sm text-blue-800 font-medium">
            💡 Ve a la sección de restaurantes y selecciona uno para comenzar
          </p>
        </div>
      </div>
    </div>
  );
}

return (
  <div className="min-h-screen bg-[#e9ecef]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header principal minimalista */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-lg border border-[#22B6B2]/10 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 sm:space-y-6 lg:space-y-0">
       
         
          {/* Tabs minimalistas */}
          <div className="bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-1 sm:p-2 border border-[#22B6B2]/20">
            <div className="flex space-x-1 sm:space-x-2">
              <button
                onClick={() => setActiveTab('categories')}
                className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold transition-all duration-300 relative overflow-hidden text-sm sm:text-base ${
                  activeTab === 'categories'
                    ? 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white shadow-lg'
                    : 'text-[#2C4358] hover:bg-white/80'
                }`}
              >
                <span className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-base sm:text-lg">🍽️</span>
                  <span className="hidden sm:inline">Categorías</span>
                  <span className="sm:hidden">Cat.</span>
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full font-bold ${
                    activeTab === 'categories' ? 'bg-white/20' : 'bg-[#22B6B2]/10'
                  }`}>
                    {categories.length}
                  </span>
                </span>
              </button>
              <button
                onClick={() => setActiveTab('items')}
                className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold transition-all duration-300 relative overflow-hidden text-sm sm:text-base ${
                  activeTab === 'items'
                    ? 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white shadow-lg'
                    : 'text-[#2C4358] hover:bg-white/80'
                }`}
              >
                <span className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-base sm:text-lg">🍕</span>
                  <span className="hidden sm:inline">Platillos</span>
                  <span className="sm:hidden">Plat.</span>
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full font-bold ${
                    activeTab === 'items' ? 'bg-white/20' : 'bg-[#22B6B2]/10'
                  }`}>
                    {menuItems.length}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de agregar */}
      <div className="flex justify-end mb-6 sm:mb-8">
        <button
          onClick={() => activeTab === 'categories' ? handleAddCategory() : handleAddMenuItem()}
          className="inline-flex items-center px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-[#f7ebd6] text-[#2C4358] font-bold rounded-xl sm:rounded-2xl hover:bg-[#f0e0c0] focus:outline-none focus:ring-4 focus:ring-[#f7ebd6]/30 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] text-sm sm:text-base"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          <span className="relative z-10 mr-2 sm:mr-3 text-lg sm:text-xl group-hover:rotate-90 transition-transform duration-300">+</span>
          <span className="relative z-10">
            <span className="hidden sm:inline">Agregar </span>
            {activeTab === 'categories' ? 'categoría' : 'platillo'}
          </span>
        </button>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-6"></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
            </div>
            <h3 className="text-lg font-bold text-[#2C4358] mb-2">Cargando menú...</h3>
            <p className="text-[#85929e]">Preparando todo para ti</p>
          </div>
        </div>
      ) : (
        <div>
          {activeTab === 'categories' ? (
            <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh] lg:max-h-[calc(100vh-400px)] pr-2 -mr-2">
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    onEdit={handleEditCategory}
                    onDelete={handleDeleteCategory}
                    // onToggleActive={handleToggleCategoryActive}
                  />
                ))}
                {categories.length === 0 && (
                  <div className="col-span-full">
                    <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center shadow-lg border border-[#22B6B2]/10">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#22B6B2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-xl sm:text-2xl">📂</span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-[#2C4358] mb-2">No hay categorías</h3>
                      <p className="text-[#85929e] mb-4 sm:mb-6 max-w-md mx-auto text-sm">
                        Crea tu primera categoría para organizar tu menú de manera profesional
                      </p>
                      <button
                        onClick={handleAddCategory}
                        className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-[#f7ebd6] text-[#2C4358] font-bold rounded-xl hover:bg-[#f0e0c0] transition-all duration-300 transform hover:scale-105 shadow-lg relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] text-sm sm:text-base"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        <span className="relative z-10 mr-2 text-lg group-hover:rotate-90 transition-transform duration-300">+</span>
                        <span className="relative z-10">Crear Primera Categoría</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 sm:p-6 shadow-lg border border-[#22B6B2]/10">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#2C4358] mb-1">Importar menú con IA</h3>
                    <p className="text-sm text-[#85929e]">
                      Sube hasta 6 imágenes del menú físico. La IA extrae platillos y los crea automáticamente.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleSelectImportImages}
                      className="px-4 py-2 bg-[#2C4358] text-white rounded-lg hover:bg-[#1f3242] transition-all text-sm font-semibold"
                    >
                      Seleccionar imágenes
                    </button>
                    <button
                      onClick={handleImportMenuFromImages}
                      disabled={importingMenu || !importImages.length}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                        importingMenu || !importImages.length
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-[#22B6B2] text-white hover:bg-[#1fa6a2]'
                      }`}
                    >
                      {importingMenu ? 'Importando...' : 'Importar'}
                    </button>
                    <button
                      onClick={handleClearImport}
                      className="px-4 py-2 bg-[#f7ebd6] text-[#2C4358] rounded-lg hover:bg-[#f0e0c0] transition-all text-sm font-semibold"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>

                {importPreviewUrls.length > 0 && (
                  <div className="mt-4 flex items-center gap-3 overflow-x-auto">
                    {importPreviewUrls.map((url, idx) => (
                      <div key={idx} className="relative group flex-shrink-0">
                        <img src={url} alt={`Menu ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border border-[#22B6B2]/20" />
                        <button
                          onClick={() => handleRemoveImportImage(idx)}
                          className="absolute top-1 right-1 bg-white/90 text-[#2C4358] rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-md opacity-0 group-hover:opacity-100 transition-all"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {loadingImportSession && (
                  <div className="mt-4 text-sm text-[#85929e]">Cargando imágenes subidas...</div>
                )}

                {importSession?.images?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-semibold text-[#2C4358] mb-2">
                      Imágenes ya subidas ({importSession.images.length})
                    </div>
                    <div className="flex items-center gap-3 overflow-x-auto">
                      {importSession.images.map((img: any) => (
                        <div key={img.id} className="relative group flex-shrink-0">
                          <img
                            src={img.signedUrl}
                            alt={img.filename}
                            className="h-16 w-24 object-cover rounded-lg border border-[#22B6B2]/20 cursor-pointer"
                            onClick={() => setImportPreviewImage({ url: img.signedUrl, name: img.filename })}
                          />
                          <button
                            onClick={() => handleDeleteImportSessionImage(img.id)}
                            className="absolute top-1 right-1 bg-white/90 text-[#2C4358] rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-md opacity-0 group-hover:opacity-100 transition-all"
                            title="Eliminar"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResult && (
                  <div className="mt-4 bg-[#22B6B2]/5 border border-[#22B6B2]/20 rounded-xl p-4 text-sm text-[#2C4358]">
                    <div className="flex flex-wrap gap-4 font-semibold">
                      <span>Extraídos: {importResult.totalExtracted}</span>
                      <span>Creados: {importResult.created.length}</span>
                      <span>Omitidos: {importResult.skipped.length}</span>
                      <span>Imágenes: {importResult.imagesProcessed}</span>
                    </div>
                    {importResult.skipped.length > 0 && (
                      <div className="mt-2 text-[#85929e]">
                        <span className="font-semibold text-[#2C4358]">Omitidos:</span>{' '}
                        {importResult.skipped.slice(0, 5).map((item, idx) => (
                          <span key={idx} className="mr-2">
                            {item.name || 'Sin nombre'} ({item.reason})
                          </span>
                        ))}
                        {importResult.skipped.length > 5 && (
                          <span>+{importResult.skipped.length - 5} más</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh] lg:max-h-[calc(100vh-400px)] pr-2 -mr-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                  {filteredMenuItems.map((item) => (
                    <MenuItemCard
                      key={item.id}
                      item={item}
                      onEdit={handleEditMenuItem}
                      onDelete={handleDeleteMenuItem}
                      onToggleAvailability={handleToggleMenuItemAvailability}
                      onManageImages={handleOpenImageModal}
                    />
                  ))}
                  {filteredMenuItems.length === 0 && (
                    <div className="col-span-full">
                      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center shadow-lg border border-[#22B6B2]/10">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#22B6B2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-xl sm:text-2xl">🍽️</span>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-[#2C4358] mb-2">
                          {selectedCategoryFilter === 'all' 
                            ? 'No hay platillos en el menú' 
                            : 'No hay platillos en esta categoría'
                          }
                        </h3>
                        <p className="text-[#85929e] mb-4 sm:mb-6 max-w-md mx-auto text-sm">
                          {selectedCategoryFilter === 'all' 
                            ? 'Comienza agregando deliciosos platillos a tu menú'
                            : 'Agrega platillos a esta categoría para que tus clientes puedan verlos'
                          }
                        </p>
                        <button
                          onClick={handleAddMenuItem}
                          className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-[#f7ebd6] text-[#2C4358] font-bold rounded-xl hover:bg-[#f0e0c0] transition-all duration-300 transform hover:scale-105 shadow-lg relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] text-sm sm:text-base"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                          <span className="relative z-10 mr-2 text-lg group-hover:rotate-90 transition-transform duration-300">+</span>
                          <span className="relative z-10">Agregar Primer Platillo</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

      {/* Modal de error global */}
      <ErrorModal
        isOpen={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onClose={() => setErrorModal({ open: false, message: '' })}
      />

      {importPreviewImage && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-4 relative">
            <button
              onClick={() => setImportPreviewImage(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 text-[#2C4358] hover:bg-white flex items-center justify-center text-lg transition-all shadow-md"
              aria-label="Cerrar"
            >
              ✕
            </button>
            {importPreviewImage.name && (
              <div className="text-sm font-semibold text-[#2C4358] mb-3">{importPreviewImage.name}</div>
            )}
            <div className="flex items-center justify-center">
              <img
                src={importPreviewImage.url}
                alt={importPreviewImage.name || 'Imagen importada'}
                className="max-h-[70vh] w-auto rounded-lg border border-[#22B6B2]/20 object-contain"
              />
            </div>
          </div>
        </div>
      )}

    {/* Modal mejorado */}
    {showAddModal && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl md:max-w-4xl lg:max-w-5xl mx-2 p-0 relative max-h-[95vh] flex flex-col border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
          {/* Header fijo */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-6 sm:px-8 py-6 rounded-t-3xl">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md hover:bg-white flex items-center justify-center text-[#2C4358] hover:text-[#22B6B2] text-2xl transition-all duration-300 z-20 group shadow-lg"
              aria-label="Cerrar"
            >
              <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
            </button>
            <div className="text-center">
              <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#2C4358] mb-2 tracking-tight">
                {editingItem ? 'Editar' : 'Crear'} {modalType === 'category' ? 'Categoría' : 'Platillo'}
              </h2>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#2C4358] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-[#85929e] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
          {/* Body scrolleable con borde redondeado */}
          <div className="overflow-hidden rounded-b-3xl flex-1 flex flex-col">
            <form onSubmit={e => { e.preventDefault(); (editingItem 
              ? (modalType === 'category' ? handleUpdateCategory : handleUpdateMenuItem)
              : (modalType === 'category' ? handleCreateCategory : handleCreateMenuItem)
            )(); }} className="relative z-10 p-8 space-y-8 overflow-y-auto h-full">
              
              {/* Nombre */}
              <div className="group">
                <label className="block text-lg font-bold text-[#2C4358] mb-3">
                  {modalType === 'category' ? 'Nombre de la categoría' : 'Nombre del platillo'} *
                </label>
                <input
                  autoFocus
                  type="text"
                  value={modalType === 'category' ? newCategoryData.name : newMenuItemData.name}
                  onChange={e => {
                    if (modalType === 'category') setNewCategoryData({ ...newCategoryData, name: e.target.value });
                    else setNewMenuItemData({ ...newMenuItemData, name: e.target.value });
                  }}
                  className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                  placeholder={modalType === 'category' ? 'Ej: Bebidas, Postres...' : 'Ej: Pizza Margherita'}
                  required
                />
                {((modalType === 'category' && !newCategoryData.name) || (modalType === 'item' && !newMenuItemData.name)) && (
                  <div className="mt-2 flex items-center space-x-2 text-[#22B6B2] font-semibold animate-pulse">
                    <span className="text-xl">⚠️</span>
                    <span>El nombre es obligatorio</span>
                  </div>
                )}
              </div>

              {/* Icono y Color para categorías */}
              {modalType === 'category' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Selector de icono */}
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">Icono de la categoría</label>
                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                      {[
                        // Básicos
                        '🍽️', '🥗', '🍲', '🥘', '🍛', '🍜', '🍝', '🍣', '🍱', '🍤', '🦐', '🦀', '🐟', '🍚', '🍙', '🍥', '🍡', '🥮', '🥠', '🥡', '🧆',
                        // Mariscos
                        '🐙', '🦑', '🦞', '🦪',
                        // Fast / callejero
                        '🍕', '🍔', '🍟', '🌭', '🥪', '🥙', '🌮', '🌯', '🫔', '🥟', '🍢',
                        // Carnes / asada
                        '🥩', '🍖', '🍗', '🥓', '🦴', '🔥', '🪵', '🌶️', '🧂', '🔪', '🍴', '🐄', '🐂', '🐃', '🐖', '🐑', '🐐', '🦬', '🐔', '🐓',
                        // Antojitos (tostadas / gorditas) y salsas
                        '🫓', '🍘', '🥣', '🥄', '🫙', '🥫', '🍯', '🫕',
                        // Desayuno / pan / tostadas (aprox)
                        '🍞', '🥖', '🥐', '🥯', '🥞', '🧇', '🍳', '🥚', '🧈',
                        // Quesos / salsas / ingredientes comunes
                        '🧀', '🥑', '🌽', '🫘', '🍅', '🧅', '🫑', '🥬', '🥕', '🍄', '🧄', '🫒', '🥒', '🥦', '🍆', '🫛',
                        // Postres / snacks
                        '🍰', '🧁', '🥧', '🍪', '🍩', '🍫', '🍬', '🍭', '🍮', '🍦', '🍧', '🍨', '🍿', '🥨', '🥜', '🌰',
                        // Frutas (para categorías tipo “Frutas” o “Smoothies”)
                        '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍍', '🥭', '🍑', '🍒', '🥝',
                        // Bebidas
                        '☕', '🍵', '🧋', '🥤', '🧃', '🥛', '🍺', '🍻', '🍷', '🥂', '🍸', '🍹', '🍾', '🧉', '🫖', '🍶', '🥃',
                        // Medidas / pesos (kilos / toneladas / etc.)
                        '⚖️', '🏋️', '🏋️‍♂️', '🏋️‍♀️', '🔢', '🧮', '📦', '🧱', '🪨', '🚚', '📊', '🏷️',
                        // Pesos explícitos (texto)
                        'Kg','¼kg', '½kg', '¾kg', '1kg', '2kg', '5kg', '10kg', '250g', '500g', '750g', '1t', '2t',
                        // Genéricos (multi-giro)
                        '🏪', '🛒', '🛍️', '🧾', '💳', '💵', '💰', '🏷️‍🔥', '📌', '📍', '🧰', '🔧', '🪛', '🔩', '🧲', '🧯', '💡', '🔌',
                        '🧴', '🧼', '🧻', '🧽', '🧹', '🧺', '🧵', '🪡', '👕', '👖', '👟', '🎁',
                      ].map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setNewCategoryData({ ...newCategoryData, icon })}
                          className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all duration-300 border-2 hover:scale-110 ${
                            newCategoryData.icon === icon
                              ? 'bg-[#22B6B2]/20 border-[#22B6B2] shadow-lg'
                              : 'bg-white border-[#22B6B2]/30 hover:border-[#22B6B2] hover:bg-[#22B6B2]/5'
                          }`}
                        >
                          <span className={/[0-9]|kg|g|t|¼|½|¾/i.test(icon) ? 'text-sm font-bold tracking-tight' : ''}>
                            {icon}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-[#85929e] mt-2 flex items-center space-x-2">
                      <span className="text-lg">🎨</span>
                      <span>Selecciona un icono que represente tu categoría</span>
                    </p>
                    <div className="mt-4">
                      <label className="block text-sm font-bold text-[#2C4358] mb-2">O pega tu icono</label>
                      <input
                        type="text"
                        value={newCategoryData.icon}
                        onChange={(e) => setNewCategoryData({ ...newCategoryData, icon: e.target.value })}
                        onBlur={() => {
                          const trimmed = (newCategoryData.icon || '').trim();
                          if (!trimmed) setNewCategoryData({ ...newCategoryData, icon: '🍽️' });
                          else if (trimmed !== newCategoryData.icon) setNewCategoryData({ ...newCategoryData, icon: trimmed });
                        }}
                        className="w-full px-4 py-3 bg-white border-2 border-[#22B6B2]/30 rounded-xl text-base font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] transition-all duration-300 shadow-md"
                        placeholder="Pega un emoji o texto corto (ej: 🥩, 🫓, ½kg)"
                        maxLength={16}
                      />
                      <p className="text-xs text-[#85929e] mt-2">
                        Nota: aquí se pega texto/emoji. Si quieres usar imágenes (PNG/SVG) como icono, requeriría otro cambio.
                      </p>
                    </div>
                  </div>

                  {/* Selector de color */}
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">Color de la categoría</label>
                    
                    {/* Preview del color actual */}
                    <div className="mb-4 p-4 bg-[#22B6B2]/5 rounded-xl border border-[#22B6B2]/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-lg cursor-pointer hover:scale-110 transition-all duration-300"
                            style={{ 
                              backgroundColor: newCategoryData.color + '20',
                              border: `2px solid ${newCategoryData.color}40`,
                              boxShadow: `0 4px 12px ${newCategoryData.color}30`
                            }}
                            onClick={handleOpenCategoryColorPicker}
                            title="Haz clic para cambiar el color"
                          >
                            <span>{newCategoryData.icon}</span>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-[#2C4358]">Preview</p>
                            <p className="text-xs text-[#85929e]">Haz clic en el icono para personalizar</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-[#85929e]">Color actual:</p>
                          <p className="font-mono font-bold text-sm" style={{ color: newCategoryData.color }}>
                            {newCategoryData.color}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Selector de colores predefinidos */}
                    <div>
                      <h4 className="text-sm font-bold text-[#2C4358] mb-3">Colores Sugeridos</h4>
                      <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 sm:gap-3">
                        {[
                          '#22B6B2', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
                          '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471',
                          '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2', '#F39C12', '#E74C3C',
                          '#9B59B6', '#3498DB', '#1ABC9C', '#F1C40F', '#E67E22', '#95A5A6'
                        ].map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setNewCategoryData({ ...newCategoryData, color })}
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-110 border-2 hover:border-[#2C4358] shadow-lg ${
                              newCategoryData.color === color 
                                ? 'border-[#2C4358] scale-110 ring-2 ring-[#22B6B2]' 
                                : 'border-white'
                            }`}
                            style={{ backgroundColor: color }}
                            title={color}
                          >
                            {newCategoryData.color === color && (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">✓</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Selector de color personalizado */}
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-[#2C4358] mb-3">Color Personalizado</h4>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={newCategoryData.color}
                          onChange={(e) => setNewCategoryData({ ...newCategoryData, color: e.target.value })}
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-[#22B6B2]/30 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                          title="Seleccionar color personalizado"
                        />
                        <div>
                          <p className="text-xs text-[#85929e]">Haz clic para elegir</p>
                          <p className="text-xs font-mono font-bold" style={{ color: newCategoryData.color }}>
                            {newCategoryData.color}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Selección de categoría */}
              {modalType === 'item' && (
                <div className="group">
                  <label className="block text-xl font-bold text-[#2C4358] mb-4 text-center">Selecciona la categoría *</label>
                  <div className="relative">
                    <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x snap-mandatory">
                      {categories.map((category) => (
                        <button
                          type="button"
                          key={category.id}
                          onClick={() => setNewMenuItemData({ ...newMenuItemData, category_id: category.id })}
                          className={`min-w-[180px] flex-shrink-0 snap-center transform transition-all duration-500 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30
                            ${newMenuItemData.category_id === category.id
                              ? 'scale-110 rotate-3 shadow-2xl'
                              : 'hover:rotate-1 shadow-lg'
                            }
                          `}
                        >
                          <div className={`relative p-6 rounded-2xl border-2 transition-all duration-300 overflow-hidden
                            ${newMenuItemData.category_id === category.id
                              ? 'bg-gradient-to-br from-[#22B6B2] to-[#2C4358] text-white border-[#22B6B2] shadow-[#22B6B2]/50'
                              : 'bg-white text-[#2C4358] border-[#22B6B2]/30 hover:border-[#22B6B2] hover:bg-white'
                            }
                          `}>
                            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-1000
                              ${newMenuItemData.category_id === category.id ? 'animate-pulse' : 'opacity-0'}
                            `}></div>
                            <div className="relative z-10 text-center">
                              <div className={`text-4xl mb-3 transition-transform duration-300
                                ${newMenuItemData.category_id === category.id ? 'scale-125' : 'group-hover:scale-110'}
                              `}>
                                {category.icon}
                              </div>
                              <h3 className={`font-bold text-lg transition-all duration-300
                                ${newMenuItemData.category_id === category.id ? 'text-white' : 'text-[#2C4358]'}
                              `}>
                                {category.name}
                              </h3>
                              {newMenuItemData.category_id === category.id && (
                                <div className="mt-2 flex justify-center">
                                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                      
                      {/* Botón para agregar nueva categoría */}
                      <button
                        type="button"
                        onClick={() => {
                          setModalType('category');
                          setEditingItem(null);
                          setNewCategoryData({ name: '', icon: '🍽️', color: '#22b6b2' });
                        }}
                        className="min-w-[180px] flex-shrink-0 snap-center transform transition-all duration-500 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30 hover:rotate-1 shadow-lg"
                      >
                        <div className="relative p-6 rounded-2xl border-2 border-dashed border-[#22B6B2]/50 bg-gradient-to-br from-[#22B6B2]/10 to-[#2C4358]/10 text-[#22B6B2] hover:border-[#22B6B2] hover:bg-gradient-to-br hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 transition-all duration-300 overflow-hidden">
                          <div className="relative z-10 text-center">
                            <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">
                              ➕
                            </div>
                            <h3 className="font-bold text-lg transition-all duration-300">
                              Nueva Categoría
                            </h3>
                            <p className="text-sm mt-1 opacity-80">
                              Crear nueva
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                    <div className="flex justify-center space-x-2 mt-4">
                      {categories.map((_, index) => (
                        <div key={index} className="w-2 h-2 bg-[#85929e] rounded-full"></div>
                      ))}
                    </div>
                  </div>
                  {!newMenuItemData.category_id && (
                    <div className="mt-4 text-center">
                      <div className="inline-flex items-center space-x-2 text-[#22B6B2] font-semibold animate-bounce">
                        <span className="text-xl">🎯</span>
                        <span>Selecciona una categoría</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Descripción */}
              {modalType === 'item' && (
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">Descripción del platillo</label>
                  <textarea
                    value={newMenuItemData.description}
                    onChange={e => setNewMenuItemData({ ...newMenuItemData, description: e.target.value })}
                    rows={3}
                    className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl resize-none"
                    placeholder="Describe el platillo, sus ingredientes principales, sabor..."
                  />
                </div>
              )}

              {/* Precio y Tiempo */}
              {modalType === 'item' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">Precio *</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl text-[#22B6B2] font-bold">$</div>
                      <input
                        type="number"
                        step="0.01"
                        value={newMenuItemData.price}
                        onChange={e => setNewMenuItemData({ ...newMenuItemData, price: e.target.value })}
                        className="w-full pl-12 pr-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-xl font-bold text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    {(!newMenuItemData.price || parseFloat(newMenuItemData.price) <= 0) && (
                      <div className="mt-2 flex items-center space-x-2 text-[#22B6B2] font-semibold">
                        <span className="text-xl">💰</span>
                        <span>El precio debe ser mayor a 0</span>
                      </div>
                    )}
                  </div>
                  <div className="group">
                    <label className="block text-lg font-bold text-[#2C4358] mb-3">Tiempo de preparación</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl">⏱️</div>
                      <input
                        type="number"
                        value={newMenuItemData.preparation_time}
                        onChange={e => setNewMenuItemData({ ...newMenuItemData, preparation_time: e.target.value })}
                        className="w-full pl-12 pr-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-xl font-bold text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                        placeholder="15"
                      />
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#85929e] font-semibold">min</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tamaños (variantes) */}
              {modalType === 'item' && (
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">Tamaños (opcional)</label>
                  <div className="space-y-3">
                    {newMenuItemData.sizes && newMenuItemData.sizes.length > 0 && newMenuItemData.sizes.map((s, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                        <input
                          type="text"
                          placeholder="Nombre (Chico, Mediano, Grande)"
                          value={s.name}
                          onChange={e => {
                            const sizes = [...newMenuItemData.sizes];
                            sizes[idx] = { ...sizes[idx], name: e.target.value };
                            setNewMenuItemData({ ...newMenuItemData, sizes });
                          }}
                          className="col-span-5 px-4 py-3 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base text-[#2C4358] focus:outline-none focus:border-[#22B6B2]"
                        />

                        <select
                          value={s.priceMode}
                          onChange={e => {
                            const sizes = [...newMenuItemData.sizes];
                            sizes[idx] = { ...sizes[idx], priceMode: e.target.value as 'absolute' | 'delta' };
                            setNewMenuItemData({ ...newMenuItemData, sizes });
                          }}
                          className="col-span-3 px-4 py-3 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base text-[#2C4358] focus:outline-none focus:border-[#22B6B2]"
                        >
                          <option value="absolute">Precio fijo</option>
                          <option value="delta">+/- sobre base</option>
                        </select>

                        <div className="col-span-3 relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22B6B2] font-bold">$</div>
                          <input
                            type="number"
                            step="0.01"
                            placeholder={s.priceMode === 'absolute' ? 'Precio' : 'Delta'}
                            value={s.value}
                            onChange={e => {
                              const sizes = [...newMenuItemData.sizes];
                              sizes[idx] = { ...sizes[idx], value: e.target.value };
                              setNewMenuItemData({ ...newMenuItemData, sizes });
                            }}
                            className="w-full pl-8 pr-4 py-3 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base text-[#2C4358] focus:outline-none focus:border-[#22B6B2]"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const sizes = (newMenuItemData.sizes || []).filter((_, i) => i !== idx);
                            setNewMenuItemData({ ...newMenuItemData, sizes });
                          }}
                          className="col-span-1 px-3 py-2 rounded-xl bg-red-100 text-red-700 hover:bg-red-200"
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          const sizes = [...(newMenuItemData.sizes || [])];
                          sizes.push({ name: '', priceMode: 'absolute', value: '' });
                          setNewMenuItemData({ ...newMenuItemData, sizes });
                        }}
                        className="px-4 py-3 bg-[#f7ebd6] text-[#2C4358] rounded-xl hover:bg-[#f0e0c0] border border-[#f7ebd6]/50 hover:border-[#f0e0c0] font-semibold"
                      >
                        + Añadir tamaño
                      </button>
                    </div>

                    <p className="text-sm text-[#85929e]">Si agregas tamaños, el precio base es el mostrado arriba; puedes usar precio fijo por tamaño o un delta (+/-) respecto al base.</p>
                  </div>
                </div>
              )}

              {/* Alérgenos */}
              {modalType === 'item' && (
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">Alérgenos</label>
                  <input
                    type="text"
                    value={newMenuItemData.allergens}
                    onChange={e => setNewMenuItemData({
                      ...newMenuItemData,
                      allergens: e.target.value
                    })}
                    className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base font-medium text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                    placeholder="Ej: Gluten, Lactosa, Frutos secos..."
                  />
                  <p className="text-sm text-[#85929e] mt-2 flex items-center space-x-2">
                    <span className="text-xl">⚠️</span>
                    <span>Informa a tus clientes sobre posibles alérgenos (separados por coma)</span>
                  </p>
                </div>
              )}

              {/* Área de imágenes - Solo para crear nuevos platillos */}
              {modalType === 'item' && !editingItem && (
                <div className="group">
                  <label className="block text-lg font-bold text-[#2C4358] mb-3">
                    Imágenes del platillo ({selectedImages.length})
                  </label>

                  <label className="inline-flex items-center gap-3 mb-4 select-none">
                    <input
                      type="checkbox"
                      checked={optimizeImagesOnCreate}
                      onChange={(e) => setOptimizeImagesOnCreate(e.target.checked)}
                      className="w-5 h-5 accent-[#22B6B2]"
                    />
                    <span className="text-sm font-semibold text-[#2C4358]">
                      Optimizar imágenes antes de subir (recomendado para mobile)
                    </span>
                  </label>
                  
                  {/* Área unificada de drag & drop */}
                  <div
                    className={`relative w-full min-h-[200px] p-6 border-2 border-dashed rounded-2xl transition-all duration-300 overflow-hidden
                      ${isDragging 
                        ? 'border-[#22B6B2] bg-[#22B6B2]/5 scale-105' 
                        : 'border-[#22B6B2]/30 bg-white hover:border-[#22B6B2] hover:bg-white'
                      }
                    `}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleSelectImages}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#22B6B2]/5 to-[#2C4358]/5 animate-pulse"></div>
                    
                    {/* Grid de imágenes de preview dentro del área de drag & drop */}
                    {selectedImages.length > 0 ? (
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-[#2C4358] flex items-center space-x-2">
                            <span className="text-2xl">🖼️</span>
                            <span>{selectedImages.length} imagen{selectedImages.length !== 1 ? 'es' : ''} seleccionada{selectedImages.length !== 1 ? 's' : ''}</span>
                          </h4>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImages([]);
                              setImagePreviewUrls([]);
                            }}
                            className="px-4 py-2 bg-[#f7ebd6] text-[#2C4358] rounded-xl hover:bg-[#f0e0c0] transition-all duration-300 font-semibold border border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                          >
                            Limpiar todas
                          </button>
                        </div>
                        
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (active.id !== over?.id) {
                              const oldIndex = selectedImages.findIndex((_, i) => `preview-${i}` === active.id);
                              const newIndex = selectedImages.findIndex((_, i) => `preview-${i}` === over?.id);
                              if (oldIndex !== -1 && newIndex !== -1) {
                                setSelectedImages((imgs) => arrayMove(imgs, oldIndex, newIndex));
                                setImagePreviewUrls((urls) => arrayMove(urls, oldIndex, newIndex));
                              }
                            }
                          }}
                        >
                          <SortableContext
                            items={selectedImages.map((_, index) => `preview-${index}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {selectedImages.map((file, index) => (
                                <SortablePreviewImage
                                  key={`preview-${index}`}
                                  id={`preview-${index}`}
                                  file={file}
                                  index={index}
                                  previewUrl={imagePreviewUrls[index]}
                                  uploadProgress={imageUploadProgress[file.name]}
                                  uploadStatus={imageUploadStatus[file.name]}
                                  onDelete={() => {
                                    setSelectedImages(prev => prev.filter((_, i) => i !== index));
                                    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
                                  }}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    ) : (
                      /* Estado vacío */
                      <div className="relative z-10 text-center py-12">
                        <div className={`text-5xl mb-4 transition-transform duration-300 ${isDragging ? 'scale-125' : 'group-hover:scale-110'}`}>
                          {isDragging ? '📁' : '📸'}
                        </div>
                        <h3 className="text-xl font-bold text-[#2C4358] mb-2">
                          {isDragging ? '¡Suelta las imágenes aquí!' : 'Arrastra y suelta imágenes aquí'}
                        </h3>
                        <p className="text-[#85929e]">
                          {isDragging ? 'Suelta para agregar las imágenes' : 'O haz clic para seleccionar archivos'}
                        </p>
                        <p className="text-sm text-[#85929e] mt-2">
                          Soporta JPG, PNG, WebP (máx. 5MB por imagen)
                        </p>
                      </div>
                    )}
                    
                    {/* Overlay de instrucciones cuando hay imágenes */}
                    {selectedImages.length > 0 && (
                      <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-xl border border-white/20">
                        <div className="flex items-center justify-center space-x-2">
                          <span>📸</span>
                          <span>Arrastra imágenes aquí para agregar más • Arrastra las imágenes para reordenar</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 pb-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-8 py-4 bg-white border-2 border-[#85929e]/30 text-[#2C4358] font-bold rounded-2xl hover:bg-[#85929e]/5 hover:border-[#85929e] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#85929e]/30"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploadingImages || (modalType === 'item' && (!newMenuItemData.name || !newMenuItemData.category_id || !newMenuItemData.price || parseFloat(newMenuItemData.price) <= 0)) || (modalType === 'category' && !newCategoryData.name)}
                  className="flex-1 px-8 py-4 bg-[#f7ebd6] text-[#2C4358] font-black rounded-2xl hover:bg-[#f0e0c0] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#f7ebd6]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <span className="relative z-10 flex items-center justify-center space-x-2">
                    {uploadingImages ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#2C4358]/30 border-t-[#2C4358] rounded-full animate-spin"></div>
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xl">{editingItem ? '💾' : '✨'}</span>
                        <span>{editingItem ? 'Actualizar' : 'Crear'}</span>
                      </>
                    )}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}

    {/* Modal de gestión de imágenes */}
    {showImageModal && selectedItemForImages && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl mx-2 p-0 relative max-h-[95vh] flex flex-col border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-[#22B6B2]/5 to-[#2C4358]/5 animate-pulse"></div>
          
          {/* Header fijo */}
          <div className="relative z-20 bg-gradient-to-r from-purple-500/10 to-[#22B6B2]/10 backdrop-blur-md border-b border-purple-500/20 px-6 sm:px-8 py-6 rounded-t-3xl">
            <button
              onClick={handleCloseImageModal}
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md hover:bg-white flex items-center justify-center text-[#2C4358] hover:text-purple-600 text-2xl transition-all duration-300 z-20 group shadow-lg"
              aria-label="Cerrar"
            >
              <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
            </button>
            <div className="text-center">
              <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-[#22B6B2] mb-2 tracking-tight">
                Gestionar Imágenes
              </h2>
              <p className="text-[#85929e] font-medium">{selectedItemForImages.name}</p>
              <div className="flex items-center justify-center space-x-2 mt-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-[#2C4358] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>

          {/* Body scrolleable */}
          <div className="relative z-10 flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-6 sm:p-8">
              <div className="space-y-6">
                {/* Loading state */}
                {loadingImages && (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-6"></div>
                        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#22B6B2] rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
                      </div>
                      <h3 className="text-lg font-bold text-[#2C4358] mb-2">Cargando imágenes...</h3>
                      <p className="text-[#85929e]">Preparando galería</p>
                    </div>
                  </div>
                )}

                {/* Contenido principal */}
                {!loadingImages && (
                  <>
                    {/* Área de agregar nuevas imágenes */}
                    <div className="group">
                      <label className="block text-lg font-bold text-[#2C4358] mb-3">
                        Agregar nuevas imágenes
                      </label>

                      <label className="inline-flex items-center gap-3 mb-4 select-none">
                        <input
                          type="checkbox"
                          checked={optimizeNewImagesOnUpload}
                          onChange={(e) => setOptimizeNewImagesOnUpload(e.target.checked)}
                          className="w-5 h-5 accent-purple-600"
                        />
                        <span className="text-sm font-semibold text-[#2C4358]">
                          Optimizar imágenes antes de subir (recomendado para mobile)
                        </span>
                      </label>
                      
                      {/* Área de drag & drop para nuevas imágenes */}
                      <div
                        className={`relative w-full min-h-[120px] p-6 border-2 border-dashed rounded-2xl transition-all duration-300 overflow-hidden cursor-pointer
                          ${isDragging 
                            ? 'border-purple-500 bg-purple-500/5 scale-105' 
                            : 'border-purple-500/30 bg-white hover:border-purple-500 hover:bg-white'
                          }
                        `}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                          const files = Array.from(e.dataTransfer.files).filter(file => 
                            file.type.startsWith('image/')
                          );
                          if (files.length > 0) {
                            setNewImagesForUpload(prev => [...prev, ...files]);
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.multiple = true;
                          input.onchange = (e) => {
                            const files = Array.from((e.target as HTMLInputElement).files || []);
                            setNewImagesForUpload(prev => [...prev, ...files]);
                          };
                          input.click();
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-[#22B6B2]/5 animate-pulse"></div>
                        
                        <div className="relative z-10 text-center py-8">
                          <div className={`text-4xl mb-4 transition-transform duration-300 ${isDragging ? 'scale-125' : 'group-hover:scale-110'}`}>
                            {isDragging ? '📁' : '📸'}
                          </div>
                          <h3 className="text-lg font-bold text-[#2C4358] mb-2">
                            {isDragging ? '¡Suelta las imágenes aquí!' : 'Arrastra y suelta imágenes aquí'}
                          </h3>
                          <p className="text-[#85929e]">
                            {isDragging ? 'Suelta para agregar las imágenes' : 'O haz clic para seleccionar archivos'}
                          </p>
                        </div>
                      </div>

                      {/* Preview de nuevas imágenes */}
                      {newImagesForUpload.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-md font-bold text-[#2C4358]">
                              Nuevas imágenes ({newImagesForUpload.length})
                            </h4>
                            <button
                              onClick={() => setNewImagesForUpload([])}
                              className="px-3 py-1 bg-red-500/10 text-red-600 rounded-lg hover:bg-red-500/20 transition-all duration-300 text-sm"
                            >
                              Limpiar
                            </button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {newImagesForUpload.map((file, index) => (
                              <SortablePreviewImage
                                key={`new-preview-${index}`}
                                id={`new-preview-${index}`}
                                file={file}
                                index={index}
                                previewUrl={URL.createObjectURL(file)}
                                uploadProgress={imageUploadProgress[file.name]}
                                uploadStatus={imageUploadStatus[file.name]}
                                onDelete={() => setNewImagesForUpload(prev => prev.filter((_, i) => i !== index))}
                              />
                            ))}
                          </div>
                          <button
                            onClick={handleUploadNewImages}
                            disabled={uploadingNewImages}
                            className="mt-4 w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {uploadingNewImages ? 'Subiendo...' : 'Subir imágenes'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Área de gestión de imágenes existentes */}
                    <div className="group">
                      <label className="block text-lg font-bold text-[#2C4358] mb-3">
                        Imágenes existentes ({itemImages.length})
                      </label>
                      
                      {itemImages.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                          <div className="text-4xl mb-4">🖼️</div>
                          <h3 className="text-lg font-bold text-[#2C4358] mb-2">No hay imágenes</h3>
                          <p className="text-[#85929e]">Agrega imágenes arriba para comenzar</p>
                        </div>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (active.id !== over?.id) {
                              const oldIndex = itemImages.findIndex((img) => img.imageUrl === active.id);
                              const newIndex = itemImages.findIndex((img) => img.imageUrl === over?.id);
                              setItemImages((imgs) => arrayMove(imgs, oldIndex, newIndex));
                            }
                          }}
                        >
                          <SortableContext
                            items={itemImages.map((img) => img.imageUrl)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                              {itemImages.map((image, index) => (
                                <SortableImage
                                  key={image.imageUrl}
                                  id={image.imageUrl}
                                  image={image}
                                  index={index}
                                  name={selectedItemForImages.name}
                                  onDelete={() => handleDeleteImage(image)}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-20 bg-gradient-to-r from-purple-500/5 to-[#22B6B2]/5 backdrop-blur-md border-t border-purple-500/20 px-6 sm:px-8 py-4 rounded-b-3xl">
            <div className="flex justify-between items-center">
              <div className="text-sm text-[#85929e]">
                {itemImages.length} imagen{itemImages.length !== 1 ? 'es' : ''} • Arrastra para reordenar
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleCloseImageModal}
                  className="px-6 py-3 bg-white border-2 border-[#85929e]/30 text-[#2C4358] font-bold rounded-xl hover:bg-[#85929e]/5 hover:border-[#85929e] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveImageOrder}
                  disabled={!selectedItemForImages || itemImages.length === 0}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-[#22B6B2] text-white font-bold rounded-xl hover:from-purple-700 hover:to-[#1a8f8c] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <span className="relative z-10 flex items-center space-x-2">
                    <span>💾</span>
                    <span>Guardar Orden</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal de confirmación de eliminación */}
    {showDeleteConfirmModal && imageToDelete && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000] animate-in fade-in duration-300">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-2 p-0 relative border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-orange-500/5 to-yellow-500/5 animate-pulse"></div>
          
          {/* Header */}
          <div className="relative z-20 bg-gradient-to-r from-red-500/10 to-orange-500/10 backdrop-blur-md border-b border-red-500/20 px-6 py-6 rounded-t-3xl">
            <div className="text-center">
              <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600 mb-2 tracking-tight">
                🗑️ Confirmar Eliminación
              </h3>
              <p className="text-[#85929e] font-medium">¿Estás seguro de que quieres eliminar esta imagen?</p>
            </div>
          </div>

          {/* Body */}
          <div className="relative z-10 p-6">
            <div className="text-center space-y-4">
              <div className="aspect-square w-32 mx-auto rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src={imageToDelete.imageUrl}
                  alt="Imagen a eliminar"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-[#2C4358] font-medium">
                Esta acción no se puede deshacer
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-20 bg-gradient-to-r from-red-500/5 to-orange-500/5 backdrop-blur-md border-t border-red-500/20 px-6 py-4 rounded-b-3xl">
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setImageToDelete(null);
                }}
                className="flex-1 px-6 py-3 bg-white border-2 border-[#85929e]/30 text-[#2C4358] font-bold rounded-xl hover:bg-[#85929e]/5 hover:border-[#85929e] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDeleteImage}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-xl hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <span>🗑️</span>
                  <span>Eliminar</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal de categorías sugeridas */}
    {showSuggestedCategoriesModal && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] animate-in fade-in duration-300">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl mx-2 p-0 relative max-h-[98vh] sm:max-h-[95vh] flex flex-col border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#f59e0b]/5 to-[#ec4899]/5 animate-pulse"></div>
          
          {/* Header */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#f59e0b]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 rounded-t-2xl sm:rounded-t-3xl">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] mb-2 tracking-tight">
                🎯 ¡Primera vez aquí!
              </h2>
              <p className="text-xs sm:text-sm lg:text-base text-[#85929e] font-medium">Te sugerimos algunas categorías para empezar</p>
              <div className="flex items-center justify-center space-x-1 sm:space-x-2 mt-2">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#22B6B2] rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#f59e0b] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#ec4899] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="relative z-10 flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="text-center mb-3 sm:mb-4 lg:mb-6">
                  <p className="text-sm sm:text-base lg:text-lg text-[#2C4358] mb-2 sm:mb-3 lg:mb-4">
                    Estas son las categorías más comunes que te ayudarán a organizar tu menú:
                  </p>
                </div>
                
                {/* Grid de categorías sugeridas */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                  {customizableCategories.map((category, index) => (
                    <div
                      key={index}
                      className="group relative bg-white/80 backdrop-blur-md rounded-lg sm:rounded-xl lg:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg border border-[#22B6B2]/10 hover:shadow-xl hover:border-[#22B6B2]/20 transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {/* Fondo con color de la categoría */}
                      <div 
                        className="absolute inset-0 rounded-lg sm:rounded-xl lg:rounded-2xl opacity-5 transition-opacity duration-300 group-hover:opacity-10"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      
                      <div className="relative z-10 text-center">
                        <div 
                          className="w-8 h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 mx-auto mb-1 sm:mb-2 lg:mb-3 rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl lg:text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg cursor-pointer"
                          style={{ 
                            backgroundColor: category.color + '20',
                            border: `2px solid ${category.color}40`,
                            boxShadow: `0 4px 12px ${category.color}30`
                          }}
                          onClick={() => handleOpenColorPicker(index)}
                          title="Haz clic para cambiar el color"
                        >
                          <span>{category.icon}</span>
                          {/* Overlay de edición */}
                          <div className="absolute inset-0 bg-black/20 rounded-lg sm:rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <span className="text-white text-xs sm:text-sm font-bold">🎨</span>
                          </div>
                        </div>
                        <h3 className="text-xs sm:text-sm lg:text-base xl:text-lg font-bold text-[#2C4358] mb-1 sm:mb-2 truncate">{category.name}</h3>
                        <div className="flex items-center justify-center space-x-1">
                          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 rounded-full animate-pulse" style={{ backgroundColor: category.color }}></div>
                          <p className="text-xs font-medium" style={{ color: category.color }}>Personalizable</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-3 sm:mt-4 lg:mt-6">
                  <p className="text-xs sm:text-sm text-[#85929e] mb-2 sm:mb-3 lg:mb-4">
                    Haz clic en los iconos para personalizar los colores • Puedes personalizar estas categorías después o crear las tuyas propias
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/5 to-[#f59e0b]/5 backdrop-blur-md border-t border-[#22B6B2]/20 px-3 sm:px-4 lg:px-6 xl:px-8 py-2 sm:py-3 lg:py-4 rounded-b-2xl sm:rounded-b-3xl">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4 justify-center">
              <button
                onClick={() => {
                  console.log('🚫 Usuario cerró modal manualmente - Marcando como mostrado');
                  setShowSuggestedCategoriesModal(false);
                  setHasShownFirstTimeModal(true); // Marcar como mostrado para que no aparezca de nuevo
                }}
                className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-white border-2 border-[#85929e]/30 text-[#2C4358] font-bold rounded-lg sm:rounded-xl lg:rounded-2xl hover:bg-[#85929e]/5 hover:border-[#85929e] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-xs sm:text-sm lg:text-base"
              >
                Crear las mías
              </button>
              <button
                onClick={handleCreateSuggestedCategories}
                className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] text-white font-bold rounded-lg sm:rounded-xl lg:rounded-2xl hover:from-[#1a8f8c] hover:to-[#d97706] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden group text-xs sm:text-sm lg:text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10 flex items-center justify-center space-x-1 sm:space-x-2">
                  <span>✨</span>
                  <span className="hidden lg:inline">Crear Categorías Sugeridas</span>
                  <span className="hidden sm:inline lg:hidden">Crear Sugeridas</span>
                  <span className="sm:hidden">Crear</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal del selector de color profesional */}
    {showColorPicker && selectedCategoryIndex !== null && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10000] animate-in fade-in duration-300">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md mx-2 p-0 relative border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#f59e0b]/5 to-[#ec4899]/5 animate-pulse"></div>
          
          {/* Header */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#f59e0b]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-4 sm:px-6 py-4 sm:py-6 rounded-t-2xl sm:rounded-t-3xl">
            <div className="text-center">
              <h3 className="text-lg sm:text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] mb-2 tracking-tight">
                🎨 Personalizar Color
              </h3>
              <p className="text-xs sm:text-sm text-[#85929e] font-medium">
                {customizableCategories[selectedCategoryIndex]?.name}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="relative z-10 p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Preview del color actual */}
              <div className="text-center">
                <div className="inline-flex items-center space-x-3 p-3 sm:p-4 bg-[#22B6B2]/5 rounded-xl border border-[#22B6B2]/10">
                  <div 
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-xl sm:text-2xl shadow-lg"
                    style={{ 
                      backgroundColor: customizableCategories[selectedCategoryIndex]?.color + '20',
                      border: `2px solid ${customizableCategories[selectedCategoryIndex]?.color}40`,
                      boxShadow: `0 4px 12px ${customizableCategories[selectedCategoryIndex]?.color}30`
                    }}
                  >
                    <span>{customizableCategories[selectedCategoryIndex]?.icon}</span>
                  </div>
                  <div className="text-left">
                    <p className="text-sm sm:text-base font-bold text-[#2C4358]">
                      {customizableCategories[selectedCategoryIndex]?.name}
                    </p>
                    <p className="text-xs sm:text-sm text-[#85929e]">
                      Color: <span className="font-mono font-bold" style={{ color: customizableCategories[selectedCategoryIndex]?.color }}>
                        {customizableCategories[selectedCategoryIndex]?.color}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Selector de colores predefinidos */}
              <div>
                <h4 className="text-sm sm:text-base font-bold text-[#2C4358] mb-3 text-center">Colores Sugeridos</h4>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 sm:gap-3">
                  {[
                    '#22B6B2', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
                    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471',
                    '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2', '#F39C12', '#E74C3C',
                    '#9B59B6', '#3498DB', '#1ABC9C', '#F1C40F', '#E67E22', '#95A5A6'
                  ].map((color, index) => (
                    <button
                      key={index}
                      onClick={() => handleUpdateCategoryColor(selectedCategoryIndex, color)}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-110 border-2 hover:border-[#2C4358] shadow-lg ${
                        customizableCategories[selectedCategoryIndex]?.color === color 
                          ? 'border-[#2C4358] scale-110 ring-2 ring-[#22B6B2]' 
                          : 'border-white'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {customizableCategories[selectedCategoryIndex]?.color === color && (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selector de color personalizado */}
              <div>
                <h4 className="text-sm sm:text-base font-bold text-[#2C4358] mb-3 text-center">Color Personalizado</h4>
                <div className="flex items-center justify-center space-x-3">
                  <input
                    type="color"
                    value={customizableCategories[selectedCategoryIndex]?.color}
                    onChange={(e) => handleUpdateCategoryColor(selectedCategoryIndex, e.target.value)}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-[#22B6B2]/30 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                    title="Seleccionar color personalizado"
                  />
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-[#85929e]">Haz clic para elegir</p>
                    <p className="text-xs font-mono font-bold" style={{ color: customizableCategories[selectedCategoryIndex]?.color }}>
                      {customizableCategories[selectedCategoryIndex]?.color}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/5 to-[#f59e0b]/5 backdrop-blur-md border-t border-[#22B6B2]/20 px-4 sm:px-6 py-3 sm:py-4 rounded-b-2xl sm:rounded-b-3xl">
            <div className="flex justify-center">
              <button
                onClick={handleCloseColorPicker}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] text-white font-bold rounded-lg sm:rounded-xl hover:from-[#1a8f8c] hover:to-[#d97706] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden group text-sm sm:text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10 flex items-center space-x-2">
                  <span>✅</span>
                  <span>Perfecto</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal del selector de color para categorías */}
    {showCategoryColorPicker && (
      <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10001] animate-in fade-in duration-300">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md mx-2 p-0 relative border border-white/20">
          {/* Fondo animado */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#f59e0b]/5 to-[#ec4899]/5 animate-pulse"></div>
          
          {/* Header */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#f59e0b]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-4 sm:px-6 py-4 sm:py-6 rounded-t-2xl sm:rounded-t-3xl">
            <div className="text-center">
              <h3 className="text-lg sm:text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] mb-2 tracking-tight">
                🎨 Personalizar Color
              </h3>
              <p className="text-xs sm:text-sm text-[#85929e] font-medium">
                {newCategoryData.name || 'Nueva categoría'}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="relative z-10 p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Preview del color actual */}
              <div className="text-center">
                <div className="inline-flex items-center space-x-3 p-3 sm:p-4 bg-[#22B6B2]/5 rounded-xl border border-[#22B6B2]/10">
                  <div 
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-xl sm:text-2xl shadow-lg"
                    style={{ 
                      backgroundColor: newCategoryData.color + '20',
                      border: `2px solid ${newCategoryData.color}40`,
                      boxShadow: `0 4px 12px ${newCategoryData.color}30`
                    }}
                  >
                    <span>{newCategoryData.icon}</span>
                  </div>
                  <div className="text-left">
                    <p className="text-sm sm:text-base font-bold text-[#2C4358]">
                      {newCategoryData.name || 'Nueva categoría'}
                    </p>
                    <p className="text-xs sm:text-sm text-[#85929e]">
                      Color: <span className="font-mono font-bold" style={{ color: newCategoryData.color }}>
                        {newCategoryData.color}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Selector de colores predefinidos */}
              <div>
                <h4 className="text-sm sm:text-base font-bold text-[#2C4358] mb-3 text-center">Colores Sugeridos</h4>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 sm:gap-3">
                  {[
                    '#22B6B2', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
                    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471',
                    '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2', '#F39C12', '#E74C3C',
                    '#9B59B6', '#3498DB', '#1ABC9C', '#F1C40F', '#E67E22', '#95A5A6'
                  ].map((color, index) => (
                    <button
                      key={index}
                      onClick={() => handleUpdateCategoryModalColor(color)}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-110 border-2 hover:border-[#2C4358] shadow-lg ${
                        newCategoryData.color === color 
                          ? 'border-[#2C4358] scale-110 ring-2 ring-[#22B6B2]' 
                          : 'border-white'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {newCategoryData.color === color && (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selector de color personalizado */}
              <div>
                <h4 className="text-sm sm:text-base font-bold text-[#2C4358] mb-3 text-center">Color Personalizado</h4>
                <div className="flex items-center justify-center space-x-3">
                  <input
                    type="color"
                    value={newCategoryData.color}
                    onChange={(e) => handleUpdateCategoryModalColor(e.target.value)}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-[#22B6B2]/30 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                    title="Seleccionar color personalizado"
                  />
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-[#85929e]">Haz clic para elegir</p>
                    <p className="text-xs font-mono font-bold" style={{ color: newCategoryData.color }}>
                      {newCategoryData.color}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/5 to-[#f59e0b]/5 backdrop-blur-md border-t border-[#22B6B2]/20 px-4 sm:px-6 py-3 sm:py-4 rounded-b-2xl sm:rounded-b-3xl">
            <div className="flex justify-center">
              <button
                onClick={handleCloseCategoryColorPicker}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-[#22B6B2] to-[#f59e0b] text-white font-bold rounded-lg sm:rounded-xl hover:from-[#1a8f8c] hover:to-[#d97706] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden group text-sm sm:text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10 flex items-center space-x-2">
                  <span>✅</span>
                  <span>Perfecto</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    <ErrorModal
      isOpen={errorModal.open}
      title={errorModal.title}
      message={errorModal.message}
      details={errorModal.details}
      onClose={() => setErrorModal({ ...errorModal, open: false })}
    />
  </div>
);
}
